/**
 * The player controller: locomotion, momentum, stamina, skills, tackles. (§3.3, §4.4, §4.6)
 *
 * ── Momentum is the whole game ──────────────────────────────────────────────
 *
 * Everything here hangs off one idea from §3.3: turn rate is an INVERSE function
 * of speed. A player at 8.5 m/s turns at 90°/s; a player at walking pace turns
 * at 360°/s. Nothing else in this file is as load-bearing. It is what makes
 * positioning matter, what makes a feint work, what makes defensive body shape a
 * skill instead of a stat, and what stops the game being air-hockey.
 *
 * The pressure to "fix" this will be constant, because a 180° at sprint costs
 * ~1.5s and that reads as unresponsive to anyone who plays for ten minutes. It
 * is not a bug. Do not linearize it, do not add a "snap turn under X m/s"
 * exception, do not scale it up "just for the ball carrier". §3.3 is explicit,
 * and the entire skill layer of the game is downstream of this one curve.
 *
 * ── Conventions ─────────────────────────────────────────────────────────────
 *
 * Angles: a heading θ maps to the direction (cos θ, sin θ) in the XZ plane, so
 * θ = datan2(z, x) is its exact inverse. `yaw` (facing) and movement headings
 * both use it. Y is up and players live on a plane.
 *
 * input.moveX/moveZ arrive ALREADY camera-relative — the client resolves the
 * camera basis before it sends them (§4.1), so they are a world-space direction
 * here and the sim never needs to know where any camera is pointing. Facing is
 * input.aimYaw. This split is why the same InputCommand works for a human, an
 * AI (§10.1), and a replay.
 *
 * ── What this file may NOT do ───────────────────────────────────────────────
 *
 * No allocation per tick (§15.7) — module-scope scratch and out-params only.
 * No Math.random (§15.3) — state.rng via pcg32. No Math.sin/cos/atan2/pow —
 * the d* wrappers from math/vec3.js. No wall clock — state.tick.
 * Animation is COSMETIC and follows physics; it never drives movement (§6.5).
 */

import type { InputCommand, StepResult, WorldState } from './types.js';
import {
  AnimState,
  FoulSeverity,
  InputFlags,
  MatchPhase,
  MAX_PLAYERS,
  SkillMove,
} from './types.js';
import { ActionKind, actionDef, isTackleKind, normalizedActionTime } from './contact-actions.js';
import { beginAnimAction, setAnimState } from './anim-exec.js';
import { ContactSocket } from './contact-sockets.js';
import {
  ACCELERATION,
  ATTRIBUTE_MAX,
  ATTRIBUTE_MIN,
  ATTRIBUTE_NEUTRAL,
  BALL_RADIUS,
  BARGE_BALL_LOOSE_EXIT_SPEED,
  BARGE_BALL_LOOSE_SPEED_GATE,
  BARGE_CONTEST_RADIUS,
  BARGE_DOMINANCE_RATIO,
  BARGE_KNOCKDOWN_S,
  BARGE_KNOCKDOWN_SPEED_GATE,
  BARGE_MIN_CLOSING_SPEED,
  BARGE_SHOVE_SPEED,
  CARRY_RECOVER_ALONG_MIN,
  CARRY_BREAK_GRACE_TICKS,
  POSSESSION_CONTROL_RADIUS,
  CARRY_TURN_FULL_PACE_YAW,
  CARRY_TURN_TIGHT_SPEED,
  CARRY_TURN_TRACKING_ALIGN,
  BARGE_STUMBLE_S,
  DECELERATION,
  FIXED_DT,
  IDLE_SETTLE_S,
  LOCO_DWELL_S,
  GRAVITY,
  INTERCEPT_AWARENESS_CONE_DEG,
  INTERCEPT_REACTION_MAX_MS,
  INTERCEPT_REACTION_MIN_MS,
  JOCKEY_LATERAL_MULTIPLIER,
  JOCKEY_SPEED_MULTIPLIER,
  JOCKEY_TARGET_RANGE,
  JUMP_GROUND_EPSILON,
  JUMP_LAND_RECOVERY_S,
  JUMP_SPEED,
  JUMP_STAMINA_COST,
  KICK_KNOCKBACK_SPEED,
  KICK_KNOCKDOWN_S,
  KNOCKDOWN_GETUP_S,
  KICK_RANGE,
  KICK_RECOVERY_S,
  MAX_SPEED_DRIBBLE,
  CLOSE_CONTROL_SPEED,
  MAX_SPEED_JOG,
  ANIM_BAND_HYSTERESIS,
  ANIM_JOG_RUN_EDGE,
  ANIM_SPRINT_SPEED,
  MAX_SPEED_RUN,
  MAX_SPEED_SPRINT,
  MAX_SPEED_SPRINT_DRIBBLE,
  PLAYER_CAPSULE_HEIGHT,
  PLAYER_CAPSULE_RADIUS,
  QUANT_POS_MAX_X,
  QUANT_POS_MAX_Z,
  PLAYER_SEPARATION_IMPULSE,
  POSSESSION_RADIUS,
  PUNCH_KNOCKBACK_SPEED,
  PUNCH_KNOCKDOWN_S,
  PUNCH_RANGE,
  PUNCH_RECOVERY_S,
  SKILL_DURATIONS_S,
  SKILL_WINDUP_FRACTION,
  SLIDE_TACKLE_RANGE,
  TACKLE_POKE_LIFT,
  TACKLE_SLIDE_LIFT,
  TACKLE_CONTACT_NORMAL_MIX,
  TACKLE_BALL_RETENTION,
  STRIKE_AIM_ALIGN_RATE,
  SLIDE_TACKLE_RECOVERY_S,
  BOOST_SPEED_MULTIPLIER,
  STAMINA_DRAIN_BOOST_1,
  STAMINA_DRAIN_BOOST_2,
  STAMINA_DRAIN_RUN,
  STAMINA_RECHARGE,
  STAMINA_RECHARGE_MAX_SPEED,
  STAMINA_MAX,
  STANDING_TACKLE_RANGE,
  STAT_EFFECT_RANGE,
  STOP_DECELERATION_PEAK,
  STOP_DECELERATION_SETTLE,
  STOP_SETTLE_SPEED,
  STUMBLE_SPEED_MULTIPLIER,
  TACKLE_CLEAN_FELL_SPEED,
  TACKLE_CLEAN_KNOCKDOWN_S,
  TACKLE_TRIP_CARRY_SPEED,
  TACKLE_TRIP_KNOCKDOWN_S,
  TACKLE_TRIP_RECKLESS_KNOCKDOWN_S,
  THROW_ANIM_HOLD_S,
  TOUCH_RADIUS,
  TRIP_MIN_OWN_MOMENTUM,
  TURN_APPROACH_GAIN,
  TURN_RATE_FLOOR_DEG,
  TURN_RATE_MAX_DEG,
  TURN_RATE_MIN_DEG,
  // §5.3 discipline — see the "Fouls & discipline" block in constants.ts.
  DOGSO_MAX_GOAL_DISTANCE,
  DOGSO_MIN_GOALWARD_DOT,
  DOGSO_MIN_GOALWARD_SPEED,
  FOUL_RECKLESS_ARRIVAL_SPEED,
  FOUL_RECKLESS_BALL_DISTANCE,
  TACKLE_FROM_BEHIND_DOT,
} from './constants.js';
// THE ball-first predicate. Not re-implemented here, and not re-implemented in
// the AI either — that duplication is what produced 137 fouls per 90. (§4.4)
import { reachesBallBeforeMan } from './tackle-geometry.js';
import type { PhysicsWorld } from './physics.js';
import { pitchBounds } from './physics.js';
import {
  DEG2RAD,
  angleDelta,
  clamp,
  clamp01,
  datan2,
  dcos,
  dsin,
  lerp,
  remapClamped,
  rotateTowards,
  wrapAngle,
} from '../math/vec3.js';
import { pcg32Float } from '../math/pcg32.js';
import { BallVelocityCause, traceBallSince } from './ball-trace.js';
import { footContact, inFootReach } from './foot-contact.js';
import { contactLogOn, logContact } from './contact-log.js';
// One-way, leaf import: stage-lock.ts imports nothing but types, so there is no
// cycle with rules/ (nothing under rules/ imports this file — only its comments
// mention it).
import { isStageLocked } from './rules/stage-lock.js';

// ---------------------------------------------------------------------------
// Tunables owned by this module.
//
// ⚠ THESE BELONG IN constants.ts. They are here only because constants.ts is
// frozen for the duration of the parallel build-out and two agents appending to
// it concurrently would lose writes. Every value below is a §-cited tunable with
// no home in constants.ts yet; MOVE THEM as soon as that file is writable, and
// bind the feel-critical ones (tackle success, skill speed) into TUNABLES so the
// leva overlay can iterate them without a rebuild (§15.5).
//
// Nothing below is inlined at a call site. That part of the rule is kept.
// ---------------------------------------------------------------------------

/** Stick/keyboard noise floor. Below this there is no movement intent. (§4.1) */
export const PLAYER_MOVE_DEADZONE = 0.08;
/**
 * Below this speed a player has no meaningful momentum and may pick any heading
 * at once. Without it, datan2 on a near-zero velocity yields a garbage heading
 * and a stationary player would refuse to set off in a new direction. (§3.3)
 */
export const MOMENTUM_MIN_SPEED = 0.25;

/** Animation cross-fade window. Cosmetic only. (§2.8) */
export const ANIM_BLEND_TIME_S = 0.15;
/** Anim/effort band: below this a player is stood still. (§3.3) */
export const ANIM_IDLE_SPEED = 0.15;
/** Anim/effort band: walking pace. Also the stamina regen boundary. (§3.3) */
export const WALK_SPEED_THRESHOLD = 1.4;
/** IDLE_SETTLE_S in ticks — derived, not a second tunable. */
const IDLE_SETTLE_TICKS = Math.ceil(IDLE_SETTLE_S / FIXED_DT);
/** LOCO_DWELL_S in ticks — derived, not a second tunable. */
const LOCO_DWELL_TICKS = Math.ceil(LOCO_DWELL_S / FIXED_DT);

/** Is this AnimState a locomotion gait? A module-scope switch rather than the
 *  `[...].includes(x)` it replaced: that literal was rebuilt for every player
 *  on every tick, which is 1 320 allocations a second in a loop §15.7 requires
 *  to allocate nothing. */
function isGait(a: number): boolean {
  return a === AnimState.Walk || a === AnimState.Jog || a === AnimState.Run
    || a === AnimState.Sprint || a === AnimState.StrafeLeft
    || a === AnimState.StrafeRight || a === AnimState.Backpedal
    || a === AnimState.Jockey
    // ⚠ CARRY TURN COUNTS AS A GAIT. It IS locomotion, and leaving it out of
    // this test reopened exactly the hole the dwell exists to close: a
    // gait -> CarryTurn -> gait sequence bypassed the damping entirely and put
    // match flaps 265 -> 501 the moment the state shipped.
    || a === AnimState.CarryTurn;
}
/** Movement this far off the facing axis reads as a strafe/shuffle. (§6.5) */
export const STRAFE_ANGLE_COS = 0.5;
/**
 * Movement further than THIS off the facing axis is a BACKPEDAL, not a strafe.
 * cos(120°): 60–120° off reads as a shuffle, past 120° the player is giving
 * ground — short backward steps, weight forward, eyes on play. (§6.5)
 */
export const BACKPEDAL_ANGLE_COS = -0.5;
/**
 * Speed cap multiplier at a DEAD backpedal (movement 180° off facing). No
 * footballer sprints backwards at forward pace.
 *
 * The ramp starts at BACKPEDAL_ANGLE_COS (120°) and reaches this at 180°, so
 * LATERAL movement is deliberately untouched: a 90°-off run is an ordinary
 * athletic stride, and the AI leans on it constantly — capping it made the
 * §4.7 box runners fail to fan apart (measured: 0.81 m of split against the
 * 4 m the test requires) because a forward facing the ball while running to a
 * post is permanently off-facing. Only genuinely backward travel is charged.
 *
 * Applied outside jockey only — jockey already carries its own multiplier and
 * charging both would double-punish the one stance that is SUPPOSED to give
 * ground. (§3.3, §4.4)
 */
export const BACKPEDAL_SPEED_CAP = 0.45;

/** Skill moves cost speed — that is half of what makes them counterable. (§4.6) */
export const SKILL_SPEED_MULTIPLIER = 0.6;
/** Recovery cost as a fraction of the move's duration. (§4.6) */
export const SKILL_RECOVERY_FRACTION = 0.35;
/**
 * Tackle success multiplier against a player inside a skill windup. This is the
 * "reward the defender for reading it" clause of §4.6, expressed as a number.
 */
export const SKILL_TACKLE_VULNERABILITY = 1.6;
/** How far each skill throws the body off the intended line, in degrees. (§4.6) */
export const BODY_FEINT_HEADING_BIAS_DEG = 55;
export const STEP_OVER_HEADING_BIAS_DEG = 40;
export const ELASTICO_HEADING_BIAS_DEG = 70;
export const BALL_ROLL_HEADING_BIAS_DEG = 25;
export const DRAG_BACK_HEADING_BIAS_DEG = 180;
export const RABONA_HEADING_BIAS_DEG = 30;

/** Base P(win) for a well-angled standing poke before any stat/geometry. (§4.4) */
export const STANDING_TACKLE_BASE_SUCCESS = 0.55;
/** A poke commits you briefly whether or not it lands. (§4.4) */
export const STANDING_TACKLE_COMMIT_S = 0.3;
/** Exit speed of a poked ball. (§4.4) */
export const STANDING_TACKLE_POKE_SPEED = 4.5;
/**
 * §6 F = HOOK tackle: instead of poking the ball AWAY, a hook draws it to the
 * defender's controllable side at a LOW exit speed, so it settles beside him and
 * his own capture takes it next tick — a controlled recovery, not a scramble. Not
 * a drag THROUGH the carrier: it is a physical hook impulse to the side.
 */
export const HOOK_TACKLE_SPEED = 2.4; // m/s — low, so the ball stays for the gather
export const HOOK_TACKLE_SIDE = 0.55; // share of the impulse thrown to the side vs back to him
/**
 * §6 E = POKE vs SLIDE split. E on the spot (this slow) is a quick standing POKE
 * that knocks the ball loose; E arriving at pace is a committed SLIDE. One button,
 * two football actions, chosen by how fast the defender is actually moving.
 */
export const POKE_VS_SLIDE_SPEED = 3.2; // m/s
/** Base P(win) for a slide that reaches the ball first. (§4.4) */
export const SLIDE_TACKLE_BASE_SUCCESS = 0.65;
/** The lunge itself, before SLIDE_TACKLE_RECOVERY_S of getting up. (§4.4) */
export const SLIDE_TACKLE_SLIDE_S = 0.5;
/**
 * The push a player gives as he leaves his feet, ON TOP of the speed he was
 * already running at.
 *
 * Watched frame by frame: at 7.5 this was not a push, it was a catapult. A
 * player jogging in at 3.9 m/s was on 8.1 m/s one frame later — the impulse
 * plus his own momentum saturating MAX_SPEED_SPRINT — so every slide, however
 * gently it was entered, fired him off at full sprint. That is the opposite of
 * what going to ground looks like: you commit at the pace you arrived, and the
 * grass takes speed OFF you from the moment you land.
 *
 * 1.4 is the shove of throwing yourself forward, no more. A slide entered at a
 * jog now stays a jog-speed slide, and one entered at a sprint still carries.
 */
export const SLIDE_TACKLE_LUNGE_SPEED = 1.4;
export const SLIDE_TACKLE_POKE_SPEED = 6.0;
/**
 * A player who has left his feet does NOT stop like one who is braking on
 * them. Studs biting turf is how DECELERATION (13 m/s²) gets its grip; a body
 * skimming on hip and thigh has less of it, and a slide tackle is supposed to
 * CARRY — that travel is the whole point of leaving your feet, and it is what
 * a viewer reads as a slide rather than a fall.
 *
 * Measured why this matters: at 18 m/s² a max-speed lunge (8.5 m/s) stopped in
 * 0.47 s, but the clip does not put the body on the grass until its CONTACT
 * beat — so the player came to a complete halt UPRIGHT and only then lay down.
 * He never slid at all. 11 m/s² stops in ~0.77 s, and with CONTACT retimed to
 * land at ~0.2 s (see SLIDE_BEATS) he is still doing ~6 m/s when he goes down,
 * so the ground phase is a real, travelling slide of roughly 1.6 m.
 */
export const SLIDE_TACKLE_DECELERATION = 11.0;
/**
 * Floor on the approach-angle factor. A tackle from a bad angle is hard, not
 * impossible — a hard zero would make side-on pokes silently never fire and read
 * as an input bug. (§4.4)
 */
export const TACKLE_ANGLE_FACTOR_MIN = 0.25;
/** Ball a full stride off the attacker's foot → the tackle is a formality. (§4.4) */
export const TACKLE_LOOSE_BALL_BONUS = 1.6;
// TACKLE_FROM_BEHIND_DOT and DOGSO_MAX_GOAL_DISTANCE used to live here. They are
// referee tunables, not controller mechanics, and they now sit with the rest of
// the discipline knobs in constants.ts §5.3 — where the ball-first tolerance and
// the reckless thresholds they are tuned ALONGSIDE also live. Tuning a card rate
// meant editing two files and it should mean editing one. Both are still
// re-exported by sim/index.ts, so no consumer moved.

/**
 * Fraction of the remaining overlap resolved positionally per tick (Baumgarte).
 * A soft constraint, not a projection: it converges over a few ticks instead of
 * teleporting, so it never hard-locks two capsules. (§3.3)
 */
export const PLAYER_SEPARATION_POSITION_CORRECTION = 0.2;
/** Speed the loser of a shoulder challenge scrubs, in m/s². (§4.4) */
export const SHOULDER_CHALLENGE_SPEED_PENALTY = 1.5;
/** Headings must agree this closely to count as "running alongside". (§4.4) */
export const SHOULDER_ALIGN_DOT = 0.6;
/** Fraction of ball speed surviving an interception, before FirstTouch. (§4.4) */
export const INTERCEPT_BALL_RETENTION = 0.25;

// ---------------------------------------------------------------------------
// The stat → sim mapping. (§8.1)
// ---------------------------------------------------------------------------

/**
 * Map an attribute (0..100) to a multiplier in [1-STAT_EFFECT_RANGE, 1+STAT_EFFECT_RANGE],
 * centred on 1.0 at ATTRIBUTE_NEUTRAL (50).
 *
 * THIS CAP IS THE BALANCE OF THE GAME (§8.1). ±15% means a maxed avatar is ~30%
 * better than a bottomed one at the extremes of a single parameter — noticeable,
 * never decisive. Skill has to beat stats, or the friends who play less quit and
 * the game has no one to play against. Every stat effect in the sim goes through
 * this function, so raising STAT_EFFECT_RANGE is the one number that could
 * quietly turn this into a pay-to-win-shaped game. It is not a feel knob.
 */
export function statMultiplier(value: number): number {
  const v = clamp(value, ATTRIBUTE_MIN, ATTRIBUTE_MAX);
  return 1 + ((v - ATTRIBUTE_NEUTRAL) / (ATTRIBUTE_MAX - ATTRIBUTE_NEUTRAL)) * STAT_EFFECT_RANGE;
}

// ---------------------------------------------------------------------------
// Skill tables. Indexed by SkillMove; built once at module load. (§15.7)
// ---------------------------------------------------------------------------

const SKILL_COUNT = 9; // SkillMove.None .. SkillMove.RabonaFakeShot

/** Base duration per move, seconds. (§4.6, SKILL_DURATIONS_S) */
const SKILL_DURATION = new Float64Array(SKILL_COUNT);
/** Speed cap multiplier while the move runs. (§4.6) */
const SKILL_SPEED_MUL = new Float64Array(SKILL_COUNT);
/** Heading the body is thrown to during the windup, radians off the intended line. */
const SKILL_BIAS = new Float64Array(SKILL_COUNT);

SKILL_DURATION[SkillMove.BodyFeintLeft] = SKILL_DURATIONS_S.BODY_FEINT;
SKILL_DURATION[SkillMove.BodyFeintRight] = SKILL_DURATIONS_S.BODY_FEINT;
SKILL_DURATION[SkillMove.BallRoll] = SKILL_DURATIONS_S.BALL_ROLL;
SKILL_DURATION[SkillMove.DragBack] = SKILL_DURATIONS_S.DRAG_BACK;
SKILL_DURATION[SkillMove.StepOver] = SKILL_DURATIONS_S.STEP_OVER;
SKILL_DURATION[SkillMove.Elastico] = SKILL_DURATIONS_S.ELASTICO;
SKILL_DURATION[SkillMove.KnockAndRun] = SKILL_DURATIONS_S.KNOCK_AND_RUN;
SKILL_DURATION[SkillMove.RabonaFakeShot] = SKILL_DURATIONS_S.RABONA_FAKE_SHOT;

SKILL_SPEED_MUL.fill(SKILL_SPEED_MULTIPLIER);
SKILL_SPEED_MUL[SkillMove.None] = 1;
/**
 * Knock-and-run is the one move that BUYS speed rather than spending it: it
 * lifts the dribble cap to the sprint cap. The cost is paid in control, not
 * tempo — the ball is knocked ahead and ball.ts's touch model (§3.4) makes it a
 * gamble. Encoding it as a cap-lift rather than a speed bonus is what keeps it
 * honest: you cannot exceed a sprint, you just stop being slowed by the ball.
 */
SKILL_SPEED_MUL[SkillMove.KnockAndRun] = MAX_SPEED_SPRINT / MAX_SPEED_DRIBBLE;

SKILL_BIAS[SkillMove.BodyFeintLeft] = -BODY_FEINT_HEADING_BIAS_DEG * DEG2RAD;
SKILL_BIAS[SkillMove.BodyFeintRight] = BODY_FEINT_HEADING_BIAS_DEG * DEG2RAD;
SKILL_BIAS[SkillMove.BallRoll] = BALL_ROLL_HEADING_BIAS_DEG * DEG2RAD;
SKILL_BIAS[SkillMove.DragBack] = DRAG_BACK_HEADING_BIAS_DEG * DEG2RAD;
SKILL_BIAS[SkillMove.StepOver] = STEP_OVER_HEADING_BIAS_DEG * DEG2RAD;
SKILL_BIAS[SkillMove.Elastico] = ELASTICO_HEADING_BIAS_DEG * DEG2RAD;
SKILL_BIAS[SkillMove.KnockAndRun] = 0;
SKILL_BIAS[SkillMove.RabonaFakeShot] = RABONA_HEADING_BIAS_DEG * DEG2RAD;

// ---------------------------------------------------------------------------
// Module-scope scratch. Never allocate in the tick. (§15.7)
// ---------------------------------------------------------------------------

/** out-param for physics.moveCharacter. */
const moveOut = { x: 0, y: 0, z: 0, grounded: false };

/** Derived per-tick locomotion inputs, filled by readIntent(). */
const intent = {
  moveMag: 0,
  desiredHeading: 0,
  sprint: false,
  jockey: false,
  /** Ctrl — close control: the ball kept tight, the pace dropped to a jog. */
  closeControl: false,
  hasMove: false,
  /**
   * We HEARD from this controller this tick. `false` is `readIntent(null)`, and
   * the distinction is load-bearing rather than bookkeeping: a null input is a
   * controller we never heard from (see readIntent), an all-zero one is a
   * player who deliberately let go. Only the second is a stop he chose, and
   * `stopRateFor` is scoped to it.
   */
  heard: false,
  /** Stamina-boost tier: 0 none, 1 (key `1`), 2 (`1` pressed twice). */
  boost: 0,
};

/**
 * Tier from the two wire bits. Tier 2 sets BOTH, so this reads 2 before it reads
 * 1 — the other order would report a tier-2 boost as tier 1 and quietly under-
 * charge it.
 */
function readBoost(flags: number): number {
  if ((flags & InputFlags.Boost2) !== 0) return 2;
  if ((flags & InputFlags.Boost1) !== 0) return 1;
  return 0;
}

// ---------------------------------------------------------------------------
// Small geometric helpers. All allocation-free.
// ---------------------------------------------------------------------------

/** Horizontal distance between two players. Players live on a plane. */
function distXZ(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

/** Effective duration of `move` for this player — Agility buys quicker feet. (§8.1) */
function skillDuration(move: number, agility: number): number {
  return SKILL_DURATION[move]! / statMultiplier(agility);
}

/**
 * True while the player is inside a skill move's WINDUP: committed, and the
 * window in which a defender who read it gets rewarded. (§4.6)
 */
function inSkillWindup(state: WorldState, id: number): boolean {
  const p = state.players;
  const move = p.skillActive[id]!;
  if (move === SkillMove.None) return false;
  const total = skillDuration(move, state.attributes[id]!.agility);
  const elapsed = total - p.skillTimer[id]!;
  return elapsed < total * SKILL_WINDUP_FRACTION;
}

/** The goal `team` is attacking, on the X axis. (§9.2) */
function targetGoalX(state: WorldState, team: number): number {
  // Allocates a PitchBounds. Only ever called from the rare foul path, never
  // from the per-tick path (§15.7).
  const b = pitchBounds(state.config);
  return ((team === 0) === state.teamAAttacksPositiveX) ? b.goalLineX : -b.goalLineX;
}

// ---------------------------------------------------------------------------
// Input → intent
// ---------------------------------------------------------------------------

/**
 * Resolve an InputCommand into the module-scope `intent` scratch.
 *
 * A null input is NOT an empty one (see step.ts): it means we never heard from
 * this controller, and "repeat last intent" is the caller's problem. We treat it
 * as no movement — which, thanks to DECELERATION, coasts to a stop rather than
 * stopping dead. A dropped packet should look like a player easing up, not like
 * a player hitting a wall.
 */
function readIntent(input: InputCommand | null): void {
  if (input === null) {
    intent.moveMag = 0;
    intent.desiredHeading = 0;
    intent.sprint = false;
    intent.jockey = false;
    intent.closeControl = false;
    intent.hasMove = false;
    intent.heard = false;
    intent.boost = 0;
    return;
  }
  intent.heard = true;
  const mx = input.moveX;
  const mz = input.moveZ;
  const mag = Math.sqrt(mx * mx + mz * mz);
  intent.hasMove = mag > PLAYER_MOVE_DEADZONE;
  // Clamp, never normalize: a half-pressed stick must stay a half-pressed stick
  // or every gamepad user walks at sprint pace. (§4.1)
  intent.moveMag = intent.hasMove ? clamp01(mag) : 0;
  intent.desiredHeading = intent.hasMove ? datan2(mz, mx) : 0;
  intent.sprint = (input.flags & InputFlags.Sprint) !== 0;
  intent.jockey = (input.flags & InputFlags.Jockey) !== 0;
  intent.closeControl = (input.flags & InputFlags.CloseControl) !== 0;
  intent.boost = readBoost(input.flags);
}

// ---------------------------------------------------------------------------
// Fouls (§5.3) — we classify and report. The rules module owns the consequence.
// ---------------------------------------------------------------------------

/**
 * Did the fouled player have an obvious goal-scoring opportunity? (§5.3)
 *
 * The real Law-12 test weighs distance to goal, DIRECTION OF PLAY, likelihood of
 * keeping control, and the number/position of defenders. All four are here now.
 * It is deliberately CONSERVATIVE — a missed DOGSO is a yellow instead of a red,
 * while a false DOGSO sends off an innocent player and ruins the match for nine
 * other people. Asymmetric costs deserve an asymmetric threshold.
 *
 * ── The criterion that was missing, and what it cost ────────────────────────
 *
 * Direction of play was NOT implemented, and the other three cannot stand in for
 * it. "In possession, inside 30 m, last man back" is true of a great many
 * ordinary passages — a winger shielding the ball toward the corner flag, a
 * forward checking back to receive, anyone moving square across the pitch. None
 * of them has an obvious goal-scoring opportunity to deny, and every one of them
 * was a straight red. MEASURED before this: 1.88 reds per 90 against a real
 * 0.15-0.25, and a DOGSO dismissal was the ONLY card the game could produce.
 *
 * So the fourth criterion is not a refinement, it is the one that carries the
 * word "obvious": the victim must actually be GOING somewhere, and that
 * somewhere must be the goal.
 */
function isDogso(state: WorldState, fouler: number, victim: number): boolean {
  const p = state.players;
  if (p.possessing[victim] !== 1) return false;

  const victimTeam = p.team[victim]!;
  const goalX = targetGoalX(state, victimTeam);
  const victimToGoal = Math.abs(goalX - p.px[victim]!);
  if (victimToGoal > DOGSO_MAX_GOAL_DISTANCE) return false;

  // LAW 12 — GENERAL DIRECTION OF PLAY. Measured against the goal's CENTRE, not
  // its X plane: a man level with the byline running straight down the touchline
  // is closing on `goalX` at full speed while going nowhere near the goal.
  const vx = p.vx[victim]!;
  const vz = p.vz[victim]!;
  const speed = Math.sqrt(vx * vx + vz * vz);
  // Below the floor his heading is noise — a stationary player's residual drift
  // points wherever the last collision left it — and a man who is not moving is
  // not running through on goal in any case.
  if (speed < DOGSO_MIN_GOALWARD_SPEED) return false;
  const toGoalX = goalX - p.px[victim]!;
  const toGoalZ = 0 - p.pz[victim]!; // the goal mouth is centred on z = 0
  const toGoalLen = Math.sqrt(toGoalX * toGoalX + toGoalZ * toGoalZ);
  if (toGoalLen <= 0) return false;
  const goalward = (vx * toGoalX + vz * toGoalZ) / (speed * toGoalLen);
  if (goalward < DOGSO_MIN_GOALWARD_DOT) return false;

  // Is the fouler the last man? The keeper does not count as cover for DOGSO.
  const foulerTeam = p.team[fouler]!;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (i === fouler || p.team[i]! !== foulerTeam) continue;
    if (state.positions[i] === 'GK') continue;
    if (Math.abs(goalX - p.px[i]!) < victimToGoal) return false; // covered
  }
  return true;
}

/**
 * Classify contact that reached the PLAYER before the BALL. (§5.3)
 *
 * Checked most-severe first. `Careless` is the floor: if you take the man and
 * not the ball, it is always at least a free kick — there is no "clean" branch
 * out of this function.
 */
function classifyFoul(
  state: WorldState,
  fouler: number,
  victim: number,
  contactSpeed: number,
  fromBehind: boolean,
  distToBall: number,
  isSlide: boolean,
): FoulSeverity {
  if (isDogso(state, fouler, victim)) return FoulSeverity.DOGSO;

  // Excessive force: a fast slide, from behind, with the ball not even in
  // playing distance — i.e. no plausible attempt to play it. That is the
  // studs-up/endangering case §5.3 wants a red for, and pinning it to "could
  // not possibly have reached the ball" keeps it out of the hands of honest
  // mistimed challenges. Deliberately left at MAX_SPEED_RUN: for a RED the
  // right bar is "faster than a man can go without sprinting", and unlike the
  // caution branch below this one is meant to be nearly unreachable.
  if (isSlide && fromBehind && contactSpeed > MAX_SPEED_RUN && distToBall > SLIDE_TACKLE_RANGE) {
    return FoulSeverity.Excessive;
  }

  // ── Reckless → yellow ─────────────────────────────────────────────────────
  //
  // Owner: "dont give red card or yellow easily", and that still binds. The
  // problem was that the previous rule — `isSlide && fromBehind` alone — was not
  // lenient, it was IMPOSSIBLE: MEASURED at 0 yellows and 0 Reckless
  // classifications across 48 minutes of 11v11, out of 56 fouls. A game whose
  // only reachable card is a straight red is exactly why cards felt brutal.
  //
  // The history, so nobody re-walks it:
  //   `fromBehind || contactSpeed > MAX_SPEED_RUN`  — too broad. Booked a
  //       defender who came across at pace for any contact, even from the front
  //       and even having touched the ball.
  //   `isSlide && fromBehind`                       — too narrow. An AI presser
  //       shapes GOAL-SIDE, i.e. in front of the man, so `fromBehind` is almost
  //       never true for the challenges that actually happen.
  //   `contactSpeed > MAX_SPEED_RUN` as a gate      — TRIED AND REVERTED, and
  //       the warning is still live: MAX_SPEED_RUN is exactly 6.0, which IS the
  //       un-sprinted top speed, and the comparison was strict — so it turned
  //       every foul Careless including a 6 m/s lunge. Any speed gate here must
  //       use FOUL_RECKLESS_ARRIVAL_SPEED, which is its own constant, sits well
  //       below 6, and is compared with `>=`.
  //
  // The middle ground is NOT a lower bar, it is a different question: was there
  // an attempt to play the ball at all? Every branch below requires the ball to
  // have been out of playing distance, so volume alone can never produce a
  // booking — a mistimed challenge with the ball at his feet stays a free kick
  // however fast he arrived. On top of that one shared requirement, three
  // recognisable bookable pictures:
  //
  //   1. off his feet, through the back of the man (unchanged);
  //   2. off his feet at all, with no prospect of the ball — leaving the ground
  //      is itself the disregard for the opponent Law 12 describes;
  //   3. arriving AT PACE and taking only the man — the late tackle.
  //
  // What is deliberately NOT here: a SLOW standing trip from behind with the
  // ball miles away. Real referees book that as stopping a promising attack, and
  // it was the first thing tried — but it turns tackle-fall.test.ts:182's
  // "a standing tackle through the man is Careless" red, and that expectation is
  // the owner's leniency directive written down. Cynical-foul detection needs
  // the promising-attack context (rules/fouls.ts already computes advantage);
  // guessing at it from geometry alone is how "yellow too easily" comes back.
  //
  // The unit test at tackle-fall.test.ts:173 is the one that catches a mistake
  // here; it pins a fast slide from behind as >= Reckless and an ordinary
  // standing tackle through the man as exactly Careless.
  if (isSlide && fromBehind) return FoulSeverity.Reckless;

  const noAttemptOnBall = distToBall > FOUL_RECKLESS_BALL_DISTANCE;
  if (noAttemptOnBall) {
    // ⚠ A SLIDE IS NOT A BOOKING BY ITSELF (owner, 2026-08-16: "dont give
    // yellow card for each slide tackle please its too frequent"). This branch
    // used to card EVERY slide that missed the ball by 1.6 m, and a slide that
    // misses is the ordinary outcome of sliding - so a clean-intentioned dive
    // in that came back yellow every time. Law 12's word is RECKLESS, and what
    // makes a missed slide reckless is the PACE he brought or the DISTANCE he
    // was from the ball, not the fact he went to ground. Off his feet through
    // the back of a man is still rule 1 above, untouched.
    if (isSlide && (contactSpeed >= FOUL_RECKLESS_ARRIVAL_SPEED
      || distToBall > FOUL_RECKLESS_BALL_DISTANCE * 2)) return FoulSeverity.Reckless;
    if (contactSpeed >= FOUL_RECKLESS_ARRIVAL_SPEED) return FoulSeverity.Reckless;
  }

  return FoulSeverity.Careless;
}

/**
 * Push a FOUL for the rules module. (§5.3)
 *
 * This allocates, which the per-tick budget (§15.7) forbids — but StepResult is
 * frozen as `RuleEvent[]` with no pool, fouls are rare (single digits per match,
 * not per tick), and step.ts clears the array every tick so nothing accumulates.
 * If RuleEvent ever gets a pool, this is the call site to change.
 */
function reportFoul(
  state: WorldState,
  out: StepResult,
  fouler: number,
  victim: number,
  severity: FoulSeverity,
): void {
  const p = state.players;
  out.events.push({
    type: 'FOUL',
    tick: state.tick,
    // The decision favours the fouled team, not the offender's.
    team: p.team[victim]!,
    player: fouler,
    victim,
    severity,
    x: p.px[victim]!,
    z: p.pz[victim]!,
  });
}

// ---------------------------------------------------------------------------
// Tackling (§4.4)
// ---------------------------------------------------------------------------

/**
 * Nearest opponent inside `range` who is worth tackling, or -1.
 * Prefers the ball carrier; falls back to the nearest opponent in range so a
 * mistimed lunge at a player who just laid the ball off still connects (and is
 * still a foul).
 */
function findTackleTarget(state: WorldState, id: number, range: number): number {
  const p = state.players;
  const team = p.team[id]!;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  let best = -1;
  let bestDist = Number.POSITIVE_INFINITY;
  let bestCarrier = false;

  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! === team) continue;
    const d = distXZ(px, pz, p.px[i]!, p.pz[i]!);
    if (d > range) continue;
    const carrier = p.possessing[i] === 1;
    // A carrier always outranks a non-carrier, regardless of distance.
    if (carrier !== bestCarrier ? carrier : d < bestDist) {
      best = i;
      bestDist = d;
      bestCarrier = carrier;
    }
  }
  return best;
}

/**
 * P(win the ball) for a challenge that reaches the ball first.
 * Success = f(Tackling, angle of approach, attacker's Dribbling, ball proximity
 * to the attacker's feet) — §4.4, in that order.
 */
function tackleSuccessChance(
  state: WorldState,
  id: number,
  target: number,
  base: number,
  isSlide: boolean,
): number {
  const p = state.players;
  const def = state.attributes[id]!;
  const atk = state.attributes[target]!;

  // Tackling is the general stat; the specific one is what you are actually
  // doing. Averaging maps BOTH to a real parameter (§8.1) instead of letting
  // standingTackle/slidingTackle rot as cosmetic duplicates of Tackling.
  const specific = isSlide ? def.slidingTackle : def.standingTackle;
  const skill = statMultiplier((def.tackling + specific) * 0.5);

  // Angle of approach: a poke you are turned away from should not land. Kept off
  // zero by TACKLE_ANGLE_FACTOR_MIN so a side-on challenge is hard, not void.
  const toTarget = datan2(p.pz[target]! - p.pz[id]!, p.px[target]! - p.px[id]!);
  const facingDot = dcos(angleDelta(p.yaw[id]!, toTarget));
  const angleFactor = remapClamped(facingDot, -1, 1, TACKLE_ANGLE_FACTOR_MIN, 1);

  // Ball proximity to the attacker's feet: a heavy touch is a free tackle, a
  // ball glued to the laces is not. TOUCH_RADIUS (§3.4) is exactly the "under
  // control" radius the dribble model uses, so the two agree by construction.
  const ballDist = distXZ(p.px[target]!, p.pz[target]!, state.ball.px, state.ball.pz);
  const proximity = remapClamped(ballDist, TOUCH_RADIUS, POSSESSION_RADIUS, 1, TACKLE_LOOSE_BALL_BONUS);

  // The attacker's Dribbling is the defence. Two ±15% stats composing to ~±35%
  // is inherent to any contest and is NOT a breach of §8.1's per-stat cap.
  let chance = (base * skill * angleFactor * proximity) / statMultiplier(atk.dribbling);

  // §4.6's promise to the defender: read the feint, win the ball.
  if (inSkillWindup(state, target)) chance *= SKILL_TACKLE_VULNERABILITY;

  return clamp01(chance);
}

/**
 * The shared "this ball is no longer anybody's" teardown. (§14)
 *
 * Every path that knocks the ball off a carrier — pokeBall, hookBall, and the
 * shoulder barge — must clear EXACTLY the same set, and each clear is here for a
 * reason somebody already paid for:
 *
 *   - `gatherOwner` is the one people forget. Leave it set and the dispossessed
 *     carrier's §1 control envelope / gather touch vacuums the ball straight back
 *     next tick, which is why a landed challenge used to feel like it "didn't win
 *     the ball". Clearing it is what makes updatePossession run an OPEN contest.
 *   - `possessing[]` is recomputed next tick by updatePossession anyway; clearing
 *     it now keeps THIS tick's speed cap and touch model honest about who has it.
 *   - `lastTouched*` is the provenance the restart rules (§5.2) read.
 *
 * Extracted rather than copied a third time: three hand-written copies of a
 * teardown is how two of them silently drift apart.
 */
function releaseBallToContest(state: WorldState, id: number): void {
  const p = state.players;
  const b = state.ball;
  b.possessedBy = -1;
  b.gatherOwner = -1;
  b.lastTouchedBy = id;
  b.lastTouchedTeam = p.team[id]!;
  b.lastTouchTick = state.tick;
  for (let i = 0; i < MAX_PLAYERS; i++) p.possessing[i] = 0;
}

/**
 * Knock the ball off the target — a CONTACT, not a teleport. (§4.4)
 *
 * This wrote `b.vx = dcos(yaw) * speed` and `b.vy = 0`: the ball's own
 * momentum was discarded, the direction was the tackler's facing whatever the
 * geometry, and nothing ever popped up. Owner, 2026-08-15: "make the contact
 * and move the ball as how it should move". Now the ball keeps part of its
 * momentum, is driven along the boot-to-ball line blended with his facing, and
 * a slide's studs lift it.
 */
function pokeBall(state: WorldState, id: number, speed: number, sliding = false): void {
  const p = state.players;
  const b = state.ball;
  const yaw = p.yaw[id]!;
  const pvx0 = b.vx, pvy0 = b.vy, pvz0 = b.vz;
  // The contact normal: from the boot toward the ball. A square hit drives it
  // on; a clipped one deflects it, which is what a real challenge looks like.
  let nx = b.px - p.px[id]!;
  let nz = b.pz - p.pz[id]!;
  const nlen = Math.sqrt(nx * nx + nz * nz);
  if (nlen > 1e-4) { nx /= nlen; nz /= nlen; } else { nx = dcos(yaw); nz = dsin(yaw); }
  const mix = TACKLE_CONTACT_NORMAL_MIX;
  let dx = nx * mix + dcos(yaw) * (1 - mix);
  let dz = nz * mix + dsin(yaw) * (1 - mix);
  const dlen = Math.sqrt(dx * dx + dz * dz);
  if (dlen > 1e-4) { dx /= dlen; dz /= dlen; } else { dx = dcos(yaw); dz = dsin(yaw); }
  b.vx = pvx0 * TACKLE_BALL_RETENTION + dx * speed;
  b.vz = pvz0 * TACKLE_BALL_RETENTION + dz * speed;
  b.vy = Math.max(0, pvy0 * TACKLE_BALL_RETENTION)
    + (sliding ? TACKLE_SLIDE_LIFT : TACKLE_POKE_LIFT);
  b.grounded = 0;
  traceBallSince(state, BallVelocityCause.FootTouch, id, pvx0, pvy0, pvz0);
  if (contactLogOn()) {
    logContact({
      tick: state.tick,
      kind: 'poke',
      player: id,
      action: 'poke',
      contact: footContact(p.px[id]!, p.pz[id]!, p.py[id]!, b.px, b.py, b.pz),
      ballBefore: [pvx0, pvy0, pvz0],
      ballAfter: [b.vx, b.vy, b.vz],
      deltaV: Math.sqrt((b.vx - pvx0) * (b.vx - pvx0) + (b.vy - pvy0) * (b.vy - pvy0) + (b.vz - pvz0) * (b.vz - pvz0)),
      possessed: false,
    });
  }
  // Leaves the ball loose and unowned (see releaseBallToContest): a landed tackle
  // has genuinely knocked it off the carrier, so updatePossession then runs an
  // OPEN contest for it (the tackler is usually best-placed).
  releaseBallToContest(state, id);
}

/**
 * §6 F = HOOK: draw the ball to the defender's controllable SIDE at a low speed —
 * a blend of "back toward me" and "around to my side" — so it settles at his feet
 * and his own updatePossession takes it next tick (a controlled recovery). Never
 * a rigid drag through the carrier: it is a physical hook impulse, and a defender
 * between the ball and its destination still collides with it in physics.
 */
function hookBall(state: WorldState, id: number, speed: number): void {
  const p = state.players;
  const b = state.ball;
  // Toward the defender (bring it back), plus a lateral component to his side.
  let tx = p.px[id]! - b.px;
  let tz = p.pz[id]! - b.pz;
  const tl = Math.sqrt(tx * tx + tz * tz) || 1;
  tx /= tl; tz /= tl;
  const perpx = -tz; // his right-ish; the exact side is cosmetic at this low speed
  const perpz = tx;
  let dirx = tx * (1 - HOOK_TACKLE_SIDE) + perpx * HOOK_TACKLE_SIDE;
  let dirz = tz * (1 - HOOK_TACKLE_SIDE) + perpz * HOOK_TACKLE_SIDE;
  const dl = Math.sqrt(dirx * dirx + dirz * dirz) || 1;
  dirx /= dl; dirz /= dl;
  const pvx0 = b.vx, pvy0 = b.vy, pvz0 = b.vz;
  b.vx = dirx * speed;
  b.vz = dirz * speed;
  b.vy = 0;
  traceBallSince(state, BallVelocityCause.FootTouch, id, pvx0, pvy0, pvz0);
  // See releaseBallToContest: the gather owner is cleared so the dispossessed
  // carrier can't immediately re-gather. The hook draws the ball to the HOOKER's
  // side, so the next-tick open contest normally resolves to him (a controlled
  // recovery).
  releaseBallToContest(state, id);
}

/**
 * Resolve one challenge at the instant it is thrown. (§4.4)
 *
 * The outcome is decided on initiation over the challenge's full reach envelope
 * (1.2m poke / 2.5m slide), not integrated over the slide. That is what the
 * ranges in §4.4 mean, it needs no extra per-player state (PlayerArrays is
 * frozen), and it makes the challenge a committed read rather than a hitbox that
 * lingers and vacuums up anyone who wanders into it.
 *
 * BALL FIRST is pure geometry, deliberately: whether you took the ball or the
 * man is not a dice roll, it is where you aimed. The roll only decides whether a
 * legal challenge actually wins the ball. Fouling is a skill failure, not luck.
 */
/**
 * Put a challenged player on the floor. (§5.3, review: "in tackle if a player
 * makes another player fall forward make that happen")
 *
 * He falls FORWARD over the challenge — carried along his OWN direction of
 * travel, not punched backwards along the tackler's facing the way
 * startViolentConduct decks a punch victim. That distinction is the whole point:
 * a trip is momentum continuing after the legs are taken away, so a runner goes
 * over the top. A player who was standing still has no momentum of his own, so
 * he falls along the tackler's line of travel instead.
 *
 * Deliberately reuses knockdownTimer + AnimState.Fallen and clears exactly what
 * startViolentConduct clears — that path is already tested, serialised and
 * hashed, and inventing a second teardown here is how the two drift apart.
 * NO RNG: a trip is a pure function of the challenge, so it cannot shift the
 * shared RNG stream and desync a replay (D1).
 */
function tripVictim(state: WorldState, id: number, victim: number, seconds: number): void {
  const p = state.players;
  const vx = p.vx[victim]!;
  const vz = p.vz[victim]!;
  const speed = Math.sqrt(vx * vx + vz * vz);
  let dx: number;
  let dz: number;
  if (speed > TRIP_MIN_OWN_MOMENTUM) {
    dx = vx / speed; // going over the top of his own run
    dz = vz / speed;
  } else {
    dx = dcos(p.yaw[id]!); // standing still — shoved along the tackle
    dz = dsin(p.yaw[id]!);
  }
  p.vx[victim] = dx * TACKLE_TRIP_CARRY_SPEED;
  p.vz[victim] = dz * TACKLE_TRIP_CARRY_SPEED;
  p.vy[victim] = 0;
  p.knockdownTimer[victim] = seconds;
  p.skillActive[victim] = SkillMove.None;
  p.skillTimer[victim] = 0;
  // A trip is an ACCEPTED action against the victim: unconditional (§11). He may
  // already read Fallen from an earlier knockdown, and being put down twice is
  // two knockdowns — two get-up lifecycles, not one that never restarts.
  beginAnimAction(p, victim, AnimState.Fallen);
}

function resolveTackle(
  state: WorldState,
  id: number,
  out: StepResult,
  range: number,
  base: number,
  pokeSpeed: number,
  isSlide: boolean,
  isHook = false,
  arrivalSpeed?: number,
): void {
  const p = state.players;
  const target = findTackleTarget(state, id, range);
  if (target < 0) return;

  // LAW 12 — a keeper in CONTROL OF THE BALL WITH HIS HANDS may not be
  // challenged at all. `gkHoldStartTick >= 0` is that state (goalkeeper.ts §5.5).
  //
  // Before this, findTackleTarget would happily pick him, the challenge landed,
  // it "won the ball", and NO foul was given — so the correct play against a
  // keeper who had gathered was to slide through him. It only looked harmless
  // because releaseBallToContest does not clear gkHoldStartTick, so stepHeldBall
  // snapped the ball back to his gloves a tick later; the challenge was still
  // being rewarded rather than punished.
  //
  // Resolved BEFORE the ball-first test on purpose: the ball is in his hands, so
  // "did the defender reach the ball first" is not a question the law asks — the
  // challenge is an offence however cleanly it was timed.
  //
  // He is deliberately NOT tripped. He is holding the ball, and flooring him
  // would put him back into the re-gather territory that broke distribution.
  if (p.gkHoldStartTick[target]! >= 0) {
    reportFoul(state, out, id, target, FoulSeverity.Careless);
    return;
  }

  const px = p.px[id]!;
  const pz = p.pz[id]!;
  const distToBall = distXZ(px, pz, state.ball.px, state.ball.pz);
  // THE ball-first verdict — tackle-geometry.ts, shared verbatim with the AI's
  // commit decision. Do not inline it back here: this exact test living in two
  // files is what produced a whistle every 40 seconds. (§4.4)
  const ballFirst = reachesBallBeforeMan(state, id, target);

  if (!ballFirst) {
    // Player before ball ⇒ foul, always. (§5.3)
    // Under contact-timing the resolve is deferred to the swing's contact frame,
    // AFTER a slide's lunge overwrote the velocity — so severity is classified on
    // the arrival speed snapshotted at commit (§2), never the lunge. The immediate
    // path passes no snapshot and reads the live velocity, which IS arrival speed
    // there (resolve runs before the lunge). Byte-identical when arrivalSpeed is
    // undefined.
    const speed = arrivalSpeed ?? Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
    // "From behind" is measured against the VICTIM's body orientation: the
    // question the law asks is whether he could see it coming.
    const victimForwardX = dcos(p.yaw[target]!);
    const victimForwardZ = dsin(p.yaw[target]!);
    const toDefX = px - p.px[target]!;
    const toDefZ = pz - p.pz[target]!;
    const len = Math.sqrt(toDefX * toDefX + toDefZ * toDefZ);
    const dot = len > 0 ? (victimForwardX * toDefX + victimForwardZ * toDefZ) / len : 0;
    const fromBehind = dot < TACKLE_FROM_BEHIND_DOT;
    const severity = classifyFoul(state, id, target, speed, fromBehind, distToBall, isSlide);
    reportFoul(state, out, id, target, severity);
    // He was fouled — so he goes DOWN. Until this, the man who got clattered kept
    // running and the whistle was the only sign anything had happened.
    // A card-worthy challenge keeps him down longer than a clumsy one. Ordered
    // after reportFoul so the restart is staged around a player already floored.
    if (severity > FoulSeverity.Clean) {
      tripVictim(state, id, target,
        severity >= FoulSeverity.Reckless
          ? TACKLE_TRIP_RECKLESS_KNOCKDOWN_S
          : TACKLE_TRIP_KNOCKDOWN_S);
    }
    return;
  }

  if (distToBall > range) return; // reached the man's side but not the ball
  if (pcg32Float(state.rng) < tackleSuccessChance(state, id, target, base, isSlide)) {
    // §6: a HOOK draws the ball to the defender for a controlled recovery; a poke/
    // slide knocks it away. Both leave it loose — §14/updatePossession then decides
    // who ends up with it (a hook usually the defender, a poke a 50/50).
    if (isHook) hookBall(state, id, HOOK_TACKLE_SPEED);
    else pokeBall(state, id, pokeSpeed, isSlide);
    // A heavy but LEGAL slide takes the man down with the ball — no foul, no
    // whistle, he just gets up. This is the ordinary football picture the review
    // was missing, and it is gated on arrival speed so a poke or a walking-pace
    // challenge never floors anybody.
    const arrival = arrivalSpeed ?? Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
    if (isSlide && arrival >= TACKLE_CLEAN_FELL_SPEED) {
      tripVictim(state, id, target, TACKLE_CLEAN_KNOCKDOWN_S);
    }
  }
  // A clean miss: no foul, but the commit timer set by the caller is the cost.
}

// ---------------------------------------------------------------------------
// Action initiation (§4.4, §4.6)
// ---------------------------------------------------------------------------

/**
 * Mid-slide or getting back up. The one state that takes steering away.
 *
 * INVARIANT: SLIDE_TACKLE_RECOVERY_S (1.2s) is longer than any other action
 * timer this file sets — the longest skill is 0.8s and its recovery is 0.28s —
 * which is what lets the timer alone separate the lunge from the recovery below.
 * If a skill ever outlasts 1.2s, this stops discriminating and the slide anim
 * will flicker. `anim` is a hashed, cloned WorldState field (world.ts), so
 * reading it here is reading sim state, not hidden state — and nothing about
 * MOVEMENT is derived from it, which is the actual §6.5 rule.
 */
function isSliding(state: WorldState, id: number): boolean {
  const p = state.players;
  if (p.actionTimer[id]! <= 0) return false;
  const a = p.anim[id]!;
  return a === AnimState.SlideTackle || a === AnimState.SlideRecover;
}

/** The ball-side tackle socket. Cosmetic for the sim (a challenge resolves through
 *  resolveTackle's own reach envelope, not a socket hit), but set so client visuals
 *  and the debug overlay can render the reaching leg. */
function tackleSocketFor(state: WorldState, id: number): ContactSocket {
  const p = state.players;
  const yaw = p.yaw[id]!;
  const rx = -dsin(yaw);
  const rz = dcos(yaw);
  const side = (state.ball.px - p.px[id]!) * rx + (state.ball.pz - p.pz[id]!) * rz;
  return side >= 0 ? ContactSocket.RightTackle : ContactSocket.LeftTackle;
}

/** §2 contact-timed challenge START: arm the action so the ball transfer / foul
 *  fires later, at the swing's contact frame (resolveContactTackle), instead of on
 *  the input tick. `arrivalSpeed` is the speed at COMMIT — foul severity is judged
 *  on it, never on a slide's lunge — stashed in the release-snapshot power slot. */
function startContactTackle(state: WorldState, id: number, kind: ActionKind, arrivalSpeed: number): void {
  const p = state.players;
  p.actKind[id] = kind;
  p.actStartTick[id] = state.tick;
  p.actExecId[id] = (p.actExecId[id]! + 1) | 0; // stale-event guard
  p.actContacted[id] = 0;
  p.actFoot[id] = tackleSocketFor(state, id);
  p.actPower[id] = arrivalSpeed; // reuse: arrival speed for deferred foul classification
  p.actTarget[id] = -1; // a challenge has no pass receiver
}

/** Resolve a contact-timed challenge at its contact frame: rebuild the challenge
 *  parameters from the ActionKind and resolve with the arrival-speed snapshot taken
 *  at commit (in actPower). Called from ball.ts stage 6 (advanceContactActions). */
export function resolveContactTackle(state: WorldState, id: number, kind: ActionKind, out: StepResult): void {
  const arrivalSpeed = state.players.actPower[id]!;
  if (kind === ActionKind.SlideTackle) {
    resolveTackle(state, id, out, SLIDE_TACKLE_RANGE, SLIDE_TACKLE_BASE_SUCCESS, SLIDE_TACKLE_POKE_SPEED, true, false, arrivalSpeed);
  } else if (kind === ActionKind.StandingTackle) {
    resolveTackle(state, id, out, STANDING_TACKLE_RANGE, STANDING_TACKLE_BASE_SUCCESS, STANDING_TACKLE_POKE_SPEED, false, true, arrivalSpeed);
  } else if (kind === ActionKind.PokeTackle) {
    resolveTackle(state, id, out, STANDING_TACKLE_RANGE, STANDING_TACKLE_BASE_SUCCESS, STANDING_TACKLE_POKE_SPEED, false, false, arrivalSpeed);
  }
}

function startSlideTackle(state: WorldState, id: number, out: StepResult): void {
  const p = state.players;

  // Arrival speed — sampled BEFORE the lunge overwrites the velocity: the force of
  // a challenge is the speed you arrived at, not the speed of the lunge itself.
  // Classifying on the lunge would make every slide foul a yellow (§5.3) and gut
  // the tackle as a defensive tool.
  const arrivalSpeed = Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
  if (state.contactTiming) {
    // §2 Deferred: arm the challenge; it fires at the contact frame (ball.ts stage 6).
    startContactTackle(state, id, ActionKind.SlideTackle, arrivalSpeed);
  } else {
    resolveTackle(state, id, out, SLIDE_TACKLE_RANGE, SLIDE_TACKLE_BASE_SUCCESS, SLIDE_TACKLE_POKE_SPEED, true);
  }

  // The lunge is an impulse along the facing. This IS an instant velocity change
  // and it is not the thing §3.3 bans: that ban is on the LOCOMOTION model, so
  // that running is never teleporting. A slide is a discrete, committed,
  // heavily-punished action — impulsive is what it is. Existing momentum along
  // the facing is preserved and capped at a human's top speed, so arriving at a
  // sprint gets you further, and lateral velocity is discarded because you are
  // now on the floor going one way.
  const fx = dcos(p.yaw[id]!);
  const fz = dsin(p.yaw[id]!);
  const along = p.vx[id]! * fx + p.vz[id]! * fz;
  const speed = Math.min(Math.max(along, 0) + SLIDE_TACKLE_LUNGE_SPEED, MAX_SPEED_SPRINT);
  p.vx[id] = fx * speed;
  p.vz[id] = fz * speed;

  p.actionTimer[id] = SLIDE_TACKLE_SLIDE_S + SLIDE_TACKLE_RECOVERY_S;
  beginAnimAction(p, id, AnimState.SlideTackle);
}

/** §6 F = HOOK / drag standing tackle — reach in and draw the ball to your side. */
function startStandingTackle(state: WorldState, id: number, out: StepResult): void {
  const p = state.players;
  if (state.contactTiming) {
    const arrivalSpeed = Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
    startContactTackle(state, id, ActionKind.StandingTackle, arrivalSpeed);
  } else {
    resolveTackle(
      state, id, out,
      STANDING_TACKLE_RANGE, STANDING_TACKLE_BASE_SUCCESS, STANDING_TACKLE_POKE_SPEED, false, true /* isHook */,
    );
  }
  p.actionTimer[id] = STANDING_TACKLE_COMMIT_S;
  beginAnimAction(p, id, AnimState.StandingTackle);
}

/** §6 E on the spot = POKE tackle — a quick standing knock-away (loose ball, no
 *  recovery), the faster/looser counterpart to the hook. Same reach + commit as a
 *  standing challenge; the poke exit speed sends the ball clear. */
function startPokeTackle(state: WorldState, id: number, out: StepResult): void {
  const p = state.players;
  if (state.contactTiming) {
    const arrivalSpeed = Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
    startContactTackle(state, id, ActionKind.PokeTackle, arrivalSpeed);
  } else {
    resolveTackle(
      state, id, out,
      STANDING_TACKLE_RANGE, STANDING_TACKLE_BASE_SUCCESS, STANDING_TACKLE_POKE_SPEED, false, false /* poke away */,
    );
  }
  p.actionTimer[id] = STANDING_TACKLE_COMMIT_S;
  // A poke chained onto a hook re-uses StandingTackle. Unconditional, so the two
  // challenges are two executions — a poke that follows a hook must swing again.
  beginAnimAction(p, id, AnimState.StandingTackle);
}

function startSkill(state: WorldState, id: number, move: number): void {
  const p = state.players;
  // Never trust the wire: `skill` is a client-supplied enum. (§2.9)
  if (move <= SkillMove.None || move >= SKILL_COUNT) return;
  const duration = skillDuration(move, state.attributes[id]!.agility);
  p.skillActive[id] = move;
  p.skillTimer[id] = duration;
  // Committed for the whole move — no chaining a feint into a slide.
  p.actionTimer[id] = duration;
  beginAnimAction(p, id, AnimState.Skill);
}

// ---------------------------------------------------------------------------
// House rule — punch / kick an opponent. (user "funny addon", 2026-07-17)
//
// NOT a Law of the Game. Contact is ALWAYS a foul — there is no clean version,
// which is the whole difference from a tackle (resolveTackle can win the ball
// cleanly; this cannot). player-controller DETECTS + KNOCKS DOWN and raises a
// distinct PUNCH/KICK event; rules/fouls.ts owns the card, the free kick, the
// per-player offence counter and the 4th-offence penalty. See DECISIONS.md D3.
//
// Fully deterministic: pure geometry + the dcos/dsin wrappers, no rng draw at
// all — a punch never rolls, it just lands. (§15.3)
// ---------------------------------------------------------------------------

/** Nearest active opponent within `range`, or -1. The MAN, never the ball. */
function findNearestOpponent(state: WorldState, id: number, range: number): number {
  const p = state.players;
  const team = p.team[id]!;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  let best = -1;
  let bestDist = range; // must be at or inside range to count
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === id) continue;
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! === team) continue;
    const d = distXZ(px, pz, p.px[i]!, p.pz[i]!);
    if (d <= bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

/**
 * Swing at the nearest opponent. The attacker is committed for a recovery window
 * (punch shorter than kick) whether or not it connects. If a victim is in range
 * they are knocked to the floor — decked along the attacker's facing, then
 * sliding to a stop over PUNCH/KICK_KNOCKDOWN_S — and a distinct PUNCH/KICK event
 * is raised for the referee (rules/fouls.ts) and the client's VFX.
 */
function startViolentConduct(state: WorldState, id: number, out: StepResult, isPunch: boolean): void {
  const p = state.players;

  // Commit the swing up front — you threw it even if you hit air.
  p.actionTimer[id] = isPunch ? PUNCH_RECOVERY_S : KICK_RECOVERY_S;
  beginAnimAction(p, id, isPunch ? AnimState.Punch : AnimState.Kick);

  const victim = findNearestOpponent(state, id, isPunch ? PUNCH_RANGE : KICK_RANGE);
  if (victim < 0) return; // swung at nobody — committed, no foul

  // Deck the victim: knocked back along the attacker's facing, planted for the
  // knockdown window. skillActive is cleared so a trick can't survive the hit.
  const yaw = p.yaw[id]!;
  const knockback = isPunch ? PUNCH_KNOCKBACK_SPEED : KICK_KNOCKBACK_SPEED;
  p.vx[victim] = dcos(yaw) * knockback;
  p.vz[victim] = dsin(yaw) * knockback;
  p.vy[victim] = 0;
  p.knockdownTimer[victim] = isPunch ? PUNCH_KNOCKDOWN_S : KICK_KNOCKDOWN_S;
  p.skillActive[victim] = SkillMove.None;
  p.skillTimer[victim] = 0;
  beginAnimAction(p, victim, AnimState.Fallen);

  out.events.push({
    type: isPunch ? 'PUNCH' : 'KICK',
    tick: state.tick,
    team: p.team[victim]!, // decision favours the struck player's team
    player: id, // the offender
    victim,
    // Straight yellow minimum for violent conduct — the escalation to a penalty
    // is the offence counter's job in rules/fouls.ts, not a bigger card here.
    severity: FoulSeverity.Reckless,
    x: p.px[victim]!,
    z: p.pz[victim]!,
  });
}

// ---------------------------------------------------------------------------
// Jump (§3.3, Attributes.jumping; house rule 2026-07-17 — Space)
//
// A jump just SEEDS the vertical velocity that moveViaPhysics already integrates
// for every player (vy += GRAVITY·dt, moved through the kinematic controller,
// landed on `grounded`). There is no new state and no world-shape change: py/vy
// already clone + hash (world.ts). Fully deterministic — no rng, pure arithmetic
// and the statMultiplier cap (§8.1). The AnimState.Jump marker is reasserted by
// stepAnim for the whole airborne arc; landing pays JUMP_LAND_RECOVERY_S.
// ---------------------------------------------------------------------------

/**
 * Grounded enough to jump: at ground level AND not descending. The vy sign check
 * is what stops a mid-air "double jump" during the brief descent tick when py
 * dips back inside the epsilon band — a settled player has vy exactly 0 (the
 * grounded clause in moveViaPhysics zeroes it), a falling one has vy < 0.
 */
function isGroundedForJump(state: WorldState, id: number): boolean {
  const p = state.players;
  // ⚠ `grounded` FIRST, and it is not decoration. The height band alone is
  // 0.03 m and the pitch undulates by exactly that (PITCH_UNDULATION_M), so a
  // player standing on a CREST measures py = 0.0388 and could not jump at all —
  // press Space on a bump and nothing happened. The controller's own contact
  // verdict does not care how high the grass is.
  if (p.grounded[id] === 1) return p.vy[id]! >= 0;
  return p.py[id]! <= JUMP_GROUND_EPSILON && p.vy[id]! >= 0;
}

/**
 * Seed a jump if Space is held from a legal state. Outfield only — a keeper's
 * aerial tool is its dedicated dive (goalkeeper.ts), which owns the GK's vertical
 * path; letting a GK also jump here would fight that integration. The other
 * legality gates (grounded, no action/knockdown, live phase) are already
 * enforced by the caller's `!wasLocked` guard, the restart input mask
 * (rules/fsm.ts), and the actionTimer re-check below.
 */
function tryJump(state: WorldState, id: number, input: InputCommand): void {
  const p = state.players;
  if ((input.flags & InputFlags.Jump) === 0) return;
  if (p.actionTimer[id]! > 0) return; // an action started this very tick — no jump
  if (state.positions[id] === 'GK') return;
  if (!isGroundedForJump(state, id)) return;

  p.vy[id] = JUMP_SPEED * statMultiplier(state.attributes[id]!.jumping);
  p.stamina[id] = Math.max(0, p.stamina[id]! - JUMP_STAMINA_COST);
  // The ONLY place this is raised. Cleared on touchdown in `moveViaPhysics`.
  p.jumpAirborne[id] = 1;
  beginAnimAction(p, id, AnimState.Jump);
}

/** One action per tick, most-committing first. */
function tryStartAction(state: WorldState, id: number, input: InputCommand, out: StepResult): void {
  const p = state.players;
  const flags = input.flags;

  if ((flags & InputFlags.SlideTackle) !== 0) {
    // §6 E — a POKE on the spot, a committed SLIDE only when arriving at pace.
    const speed = Math.sqrt(p.vx[id]! * p.vx[id]! + p.vz[id]! * p.vz[id]!);
    if (speed >= POKE_VS_SLIDE_SPEED) startSlideTackle(state, id, out);
    else startPokeTackle(state, id, out);
    return;
  }
  // House rule: punching / kicking an opponent needs no ball — you just swing.
  if ((flags & InputFlags.PunchPlayer) !== 0) {
    startViolentConduct(state, id, out, true);
    return;
  }
  if ((flags & InputFlags.KickPlayer) !== 0) {
    startViolentConduct(state, id, out, false);
    return;
  }
  if ((flags & InputFlags.StandingTackle) !== 0 && p.possessing[id] !== 1) {
    startStandingTackle(state, id, out);
    return;
  }
  // Skill moves are ball moves: no ball at your feet, no trickery. (§4.6)
  if (input.skill !== SkillMove.None && p.possessing[id] === 1) {
    startSkill(state, id, input.skill);
  }
}

// ---------------------------------------------------------------------------
// Interception (§4.4)
// ---------------------------------------------------------------------------

/** Reaction base: the midpoint of §4.4's band. Derived, not a new tunable. */
const INTERCEPT_REACTION_BASE_MS = (INTERCEPT_REACTION_MIN_MS + INTERCEPT_REACTION_MAX_MS) * 0.5;

/** Distance from point (px,pz) to segment (ax,az)-(bx,bz), in the XZ plane. */
function pointSegmentDistXZ(
  px: number, pz: number,
  ax: number, az: number,
  bx: number, bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  if (lenSq === 0) return distXZ(px, pz, ax, az);
  let t = ((px - ax) * dx + (pz - az) * dz) / lenSq;
  t = clamp01(t);
  return distXZ(px, pz, ax + dx * t, az + dz * t);
}

/**
 * Intercept a loose ball whose path crosses this player. (§4.4)
 *
 * Two things stop this being a magnet, which is the explicit §4.4 requirement:
 * an awareness cone (you cannot intercept what you cannot see) and a reaction
 * gate on Reactions.
 *
 * ── The reaction model, and where it deviates ───────────────────────────────
 * §4.4 wants a reaction DELAY: the ball enters your awareness, and ~120-320ms
 * later you may play it. A delay needs per-player state ("when did I first see
 * it") and PlayerArrays is frozen — there is nowhere to put it, and a
 * module-scope array would be hidden state that neither clones nor hashes, which
 * is precisely the desync world.ts warns about. So instead of storing the clock,
 * we roll for it: P(react this tick) = dt / delay makes the WAIT until reaction
 * geometric with a mean of exactly `delay`. Same expected delay, same stat
 * sensitivity, no state, and deterministic through state.rng.
 *
 * The honest cost: the delay is memoryless, so a defender who has tracked the
 * ball for a second is no readier than one who just turned around. Correct
 * behaviour would need one Float64 of state per player.
 *
 * The 120-320ms band is used as a CLAMP, not as the stat mapping. Mapping the
 * stat across the full band would be a ±45% swing and blow §8.1's ±15% cap,
 * which the brief calls essential twice. Base is the band's midpoint and the
 * stat moves it ±15%; the band stays the hard envelope.
 */
function tryIntercept(state: WorldState, id: number): void {
  const p = state.players;
  const b = state.ball;

  if (p.possessing[id] === 1) return; // already yours
  if (b.possessedBy !== -1) return; // someone is dribbling it — that's §3.4's job
  if (b.lastTouchTick === state.tick) return; // already touched this tick; don't double-resolve
  if (b.py > PLAYER_CAPSULE_HEIGHT) return; // over your head

  const attr = state.attributes[id]!;
  const px = p.px[id]!;
  const pz = p.pz[id]!;

  // Interceptions buys reach — the difference between a toe and a shin. (§8.1)
  const reach = (PLAYER_CAPSULE_RADIUS + BALL_RADIUS) * statMultiplier(attr.interceptions);

  // Swept against the ball's path this tick, not its current point: a 25 m/s
  // pass moves 0.42m per tick and a point test would miss the leg entirely.
  const dist = pointSegmentDistXZ(px, pz, b.px, b.pz, b.px + b.vx * FIXED_DT, b.pz + b.vz * FIXED_DT);
  if (dist > reach) return;

  const toBall = datan2(b.pz - pz, b.px - px);
  if (Math.abs(angleDelta(p.yaw[id]!, toBall)) > INTERCEPT_AWARENESS_CONE_DEG * 0.5 * DEG2RAD) return;

  const delayS = clamp(
    INTERCEPT_REACTION_BASE_MS / statMultiplier(attr.reactions),
    INTERCEPT_REACTION_MIN_MS,
    INTERCEPT_REACTION_MAX_MS,
  ) * 0.001;
  if (pcg32Float(state.rng) > FIXED_DT / delayS) return;

  // A touch, not a catch. FirstTouch decides how dead it lands: a poor one
  // squirts away and the 50/50 stays live. No ball-glue, ever. (§3.4)
  const retention = INTERCEPT_BALL_RETENTION / statMultiplier(attr.firstTouch);
  const ivx0 = b.vx, ivy0 = b.vy, ivz0 = b.vz;
  b.vx *= retention;
  b.vy *= retention;
  b.vz *= retention;
  traceBallSince(state, BallVelocityCause.FootTouch, id, ivx0, ivy0, ivz0);
  if (contactLogOn()) {
    logContact({
      tick: state.tick,
      kind: 'intercept',
      player: id,
      action: 'intercept',
      contact: footContact(px, pz, p.py[id]!, b.px, b.py, b.pz),
      ballBefore: [ivx0, ivy0, ivz0],
      ballAfter: [b.vx, b.vy, b.vz],
      deltaV: Math.sqrt((b.vx - ivx0) * (b.vx - ivx0) + (b.vy - ivy0) * (b.vy - ivy0) + (b.vz - ivz0) * (b.vz - ivz0)),
      possessed: false,
    });
  }
  b.lastTouchedBy = id;
  b.lastTouchedTeam = p.team[id]!;
  b.lastTouchTick = state.tick;
}

// ---------------------------------------------------------------------------
// Locomotion (§3.3) — the heart of it
// ---------------------------------------------------------------------------

/**
 * THE CURVE (§3.3). Turn rate falls from 360°/s at a standstill to 90°/s at
 * full sprint. Everything good about this game's movement is downstream of these
 * three lines, and every request to "make turning feel snappier" is a request to
 * delete the skill layer. Agility is the only thing that touches it, capped at
 * ±15% like everything else (§8.1).
 */
function turnRateFor(speed: number, agility: number): number {
  const deg = remapClamped(speed, 0, MAX_SPEED_SPRINT, TURN_RATE_MAX_DEG, TURN_RATE_MIN_DEG);
  return deg * DEG2RAD * statMultiplier(agility);
}

/**
 * THE FACING RATE, which is `turnRateFor` with the approach ease on it.
 * (§3.3; see TURN_APPROACH_GAIN in constants.ts for the measurement.)
 *
 * `turnRateFor` is a CEILING. This is what the shoulders actually do: the same
 * ceiling, scaled down as the residual closes, floored so the last fraction of a
 * degree terminates on a tick instead of asymptotically never.
 *
 * ⚠ It takes the residual in RADIANS and returns radians/s, like everything else
 * here; `TURN_APPROACH_GAIN` is per-second and therefore unit-free across that
 * conversion, which is why it is not written in degrees.
 *
 * ⚠ FACING ONLY. The MOVEMENT heading keeps the bare ceiling — that rotation is
 * the momentum model (§3.3), it is already charged for through the cos-scrub one
 * screen down, and easing it as well charges twice for the same turn. This is
 * the same "do not charge twice" rule CARRY_TURN_TRACKING_ALIGN records.
 */
function facingRateFor(speed: number, agility: number, residual: number): number {
  const ceiling = turnRateFor(speed, agility);
  const eased = TURN_APPROACH_GAIN * (residual < 0 ? -residual : residual);
  const floor = TURN_RATE_FLOOR_DEG * DEG2RAD;
  return Math.min(ceiling, Math.max(floor, eased));
}

/**
 * How hard he brakes when he has asked for NOTHING — the free stop. (§3.3)
 *
 * Full effort while there is momentum to kill, easing toward a settle over the
 * last `STOP_SETTLE_SPEED`. See the STOP_DECELERATION_PEAK block in constants.ts
 * for the before/after tape, for why this is a pure function of speed rather
 * than a ramp with a clock in it, and — the part that has already bitten once —
 * for why a SCRUB keeps the flat `DECELERATION` instead.
 *
 * Not stat-modulated, for the same reason the flat number never was: §8.1 wants
 * every stat to map to a real parameter, not every parameter to carry a stat.
 * Stopping is physics.
 */
function stopRateFor(speed: number): number {
  const t = speed >= STOP_SETTLE_SPEED ? 1 : speed / STOP_SETTLE_SPEED;
  return STOP_DECELERATION_SETTLE + (STOP_DECELERATION_PEAK - STOP_DECELERATION_SETTLE) * t;
}

/**
 * FATIGUE TAKES THE TOP GEAR, NOT THE LEGS. (§3.3)
 *
 * ⚠ THIS WAS A MULTIPLIER AND THE OWNER FILMED IT THREE TIMES: "he is going
 * slow after some point why?", "he starts getting slow in middle of run why?",
 * "slowing down idk why". Every one of those was this function. Measured in a
 * driven capture (`dirs`, 1.80→3.10 s, W held the whole way): his speed fell
 * 5.474 → 5.375 m/s, a ratio of 0.982, while the fatigue multiplier fell 0.995
 * → 0.977 — a ratio of 0.982. Same number to three decimals.
 *
 * Scaling the WHOLE cap means the drift is continuous, applies at every pace,
 * and is invisible: he holds one key at a steady jog and quietly decelerates.
 * A previous pass answered that by PRINTING the multiplier on the HUD, and he
 * filmed it again anyway — a slowdown you can read off a number is still a
 * slowdown you did not ask for.
 *
 * So fatigue is now a CEILING. Below the threshold the top speed available
 * falls from a sprint toward a steady run, and never below a steady run: an
 * empty bar means "you cannot sprint", which is what being knackered does to a
 * footballer. Ordinary running and dribbling (caps 5.5–6.0) are untouched at
 * any stamina, so nothing decays while a key is held at ordinary pace. The
 * penalty only appears when he ASKS for the top gear and does not get it —
 * which is legible, because he is holding sprint at the time.
 */
function fatigueCeiling(cap: number, stamina: number): number {
  // ⚠ A CLIFF AT EMPTY, NOT A RAMP. Owner, after seeing the ramp version:
  // "players should never slow down unless the player stops or stamina is
  // over". A ramp starting at STAMINA_FATIGUE_THRESHOLD is still a slowdown he
  // did not ask for and cannot feel the cause of — it just starts later. So
  // there is exactly one speed penalty in the game and it has exactly one
  // trigger: the bar reaching zero.
  if (stamina > 0) return cap;
  return Math.min(cap, MAX_SPEED_RUN);
}

/**
 * THE CARRY GOVERNOR (§3.3 / §3.4) — "you cannot run at full pace when the ball
 * is not at your feet."
 *
 * True when this player still OWNS the ball but has run off it: the ball is out
 * of his boot's reach AND no longer ahead of him along his running line. His cap
 * then drops BELOW THE BALL'S OWN SPEED — he has to check his run and get back on
 * it, which is what a footballer does and what makes the ball recoverable.
 *
 * Why this is load-bearing rather than polish (measured, dribble-carry-separation
 * .test.ts): the gather's pace-match cancels the ball's RECESSION from the
 * carrier, whatever the ball's bearing. With the ball behind him that is a leash
 * — the ball is dragged along at exactly his speed — and the wide control
 * envelope (2.35 m) keeps `possessedBy` on him, so no recapture path ever runs
 * and no touch can fire (out of foot reach, and a knock never fires backwards
 * through his own body). Holding sprint pace there is a STABLE FIXED POINT: the
 * ball sat 1.7 m behind a sprinting carrier for 216 consecutive ticks. Slowing
 * him is what breaks it: the ball is no longer receding, so it closes, reaches
 * his boot and gets touched forward again.
 *
 * It also closes the long-documented cap-jump (§6): the tick a knock ran the ball
 * past the tight capture gate, `possessing` fell to 0, the cap jumped 7.0 → 8.5
 * (free sprint) and the carrier accelerated AWAY from his own ball. The gather
 * owner is the reliable "this is still his carry" signal that fix needed.
 *
 * Deliberately narrow: a normal knock-and-run has the ball out in FRONT (along
 * ≈ +0.85 m at sprint), so this never touches it — the 7.0 m/s sprint-dribble is
 * untouched. No RNG, no new state: pure geometry off WorldState. (D1)
 */
/**
 * "This is still HIS carry." He owns the gather AND either possesses the ball or
 * it is momentarily loose — the knock-and-run blip, where a knock runs the ball
 * just past the tight capture gate for a tick or two and `possessedBy` falls to
 * -1 while the dribble is plainly still his. A KICK clears `gatherOwner`
 * (applyStrike) and a landed tackle clears it too, so a genuinely played or lost
 * ball is never this; a rival winning it reassigns the owner.
 *
 * Live play only: during a restart the ball belongs to the rules layer and
 * `gatherOwner` may still name the pre-stoppage carrier.
 */
function isHisCarry(state: WorldState, id: number): boolean {
  const b = state.ball;
  if (state.match.phase !== MatchPhase.InPlay) return false;
  if (b.gatherOwner !== id) return false;
  if (b.possessedBy >= 0 && b.possessedBy !== id) return false;
  return true;
}

function carryHasBrokenDown(state: WorldState, id: number): boolean {
  const b = state.ball;
  if (!isHisCarry(state, id)) return false;
  const p = state.players;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  // At his feet ⇒ nothing has broken down, whatever the bearing.
  if (inFootReach(px, pz, p.py[id]!, b.px, b.py, b.pz)) return false;
  // Ahead of him along the line he is RUNNING (not facing: he may be looking
  // anywhere). Using velocity also means that the moment he turns back toward
  // his own ball the offset goes positive and the cap is released — this slows a
  // man running AWAY from his ball, never one going back for it.
  const vx = p.vx[id]!;
  const vz = p.vz[id]!;
  const speed = Math.sqrt(vx * vx + vz * vz);
  if (speed < 1e-3) return false;
  const along = ((b.px - px) * vx + (b.pz - pz) * vz) / speed;
  return along < CARRY_RECOVER_ALONG_MIN;
}

/**
 * The LATCHED form of the test above, and the one `maxSpeedFor` actually asks.
 *
 * ⚠ THE RAW GEOMETRIC TEST CHATTERS, AND THE CHATTER IS THE DEFECT. A carrier
 * drifting alongside his own ball crosses the "is it still ahead of me" line
 * over and over, and every crossing swings the cap between full pace and
 * CARRY_RECOVER_MIN_SPEED. The gait ladder follows the cap, so the body changes
 * clip several times a second. Filmed by the owner as "randomly jumping when he
 * has the ball" and "slowing down idk why" — one cause, reported as two faults.
 *
 * So the geometry ENGAGES the latch and only getting the ball back CLEARS it.
 * Recovery is FOOT REACH, not the bearing test: coming back to within a boot of
 * the ball is a thing the player can actually achieve and see, whereas releasing
 * on the bearing hands the cap back the instant he happens to point at it, which
 * is what let the oscillation start again.
 *
 * Also cleared the moment he is no longer the carrier at all — conceding the
 * ball ends the recovery rather than leaving a stale latch on a player who has
 * nothing to recover.
 */
function releaseBrokenCarry(state: WorldState, id: number): void {
  const p = state.players;
  const b = state.ball;
  if (!isHisCarry(state, id)) {
    p.carryRecovering[id] = 0;
    return;
  }
  // ⚠ A STRICTER TEST THAN THE OLD GOVERNOR'S, ON PURPOSE. `carryHasBrokenDown`
  // was tuned to trigger a mild SLOWDOWN; taking a man's ball off him is a far
  // heavier consequence and must not fire on the same hair trigger. Judged by
  // dribble-envelope's S-curve acceptance: with the bearing test alone he shed
  // the ball mid-curve and ran 39.3 m from it. The ball has to be outside the
  // carrier's own keep-radius before "he has run off it" is even arguable.
  const dBall = Math.hypot(b.px - p.px[id]!, b.pz - p.pz[id]!);
  if (dBall <= POSSESSION_CONTROL_RADIUS || !carryHasBrokenDown(state, id)) {
    p.carryRecovering[id] = 0;
    return;
  }

  // ⚠ A GRACE PERIOD, AND IT IS NOT OPTIONAL. The geometric test CHATTERS — a
  // carrier drifting alongside his ball crosses the "is it still ahead of me"
  // line repeatedly, and a player TURNING has the ball behind his new running
  // line for a few ticks by definition. The old governor answered that chatter
  // by latching a speed cap; answering it by handing the ball away instantly
  // would be strictly worse, trading a speed flicker for a POSSESSION flicker.
  // So the break has to persist before it costs him anything.
  const held = p.carryRecovering[id]! + 1;
  if (held < CARRY_BREAK_GRACE_TICKS) {
    p.carryRecovering[id] = held;
    return;
  }

  // He has run off his own ball. CUT THE LEASH: clearing `gatherOwner` is what
  // stops step_() pace-matching the ball to him, so it stays where he left it
  // and decelerates on the grass like any loose ball. Possession goes with it,
  // because the 2.35 m control envelope is exactly what was keeping every
  // recapture path from running.
  //
  // He is not slowed by a single m/s. He simply no longer has the ball, and has
  // to turn and win it back like anyone else — which is the honest cost of
  // knocking it too far, and is visible without a HUD telling him about it.
  b.gatherOwner = -1;
  if (b.possessedBy === id) b.possessedBy = -1;
  p.possessing[id] = 0;
  p.carryRecovering[id] = 0;
}

/**
 * The speed cap for this player, this tick. (§3.3)
 *
 * Note the ladder is multiplicative and order-independent, and that the dribble
 * cap is a MIN, not a replacement: sprinting with the ball gives 5.5 m/s and
 * without it 8.5 m/s. That gap is the knock-it-past-him decision, and it exists
 * for free just by taking the smaller of the two.
 */
function maxSpeedFor(state: WorldState, id: number): number {
  const p = state.players;
  const attr = state.attributes[id]!;

  // ── CLOSE CONTROL (Ctrl) ────────────────────────────────────────────────
  //
  // Owner: "when player is running with ball and he press ctrl he takes the
  // ball with dribbling". It caps him inside the JOG band, which is what makes
  // a close dribble close — you cannot keep the ball on your boot at a sprint.
  //
  // It is also the answer to "stamina is burning on walk and not getting
  // recharged on walk". There was no walk to burn on: a keyboard sends
  // moveMag 1.0, so every step was a full 6 m/s RUN and drained, and the free
  // band (WALK_SPEED_THRESHOLD..MAX_SPEED_JOG) was reachable only by a gamepad
  // half-press. MEASURED: key press 6.00 m/s at -1.45 stamina/s, half stick
  // 3.00 m/s at +0.00. Close control puts a keyboard player in that band, so
  // the key that tightens the dribble is the key that lets him breathe.
  //
  // Sprint outranks it — pressing both is a knock-and-run, and the ball
  // running loose is the price, exactly as it is without Ctrl.
  let cap = intent.sprint ? MAX_SPEED_SPRINT : MAX_SPEED_RUN;
  if (intent.closeControl && !intent.sprint) cap = Math.min(cap, CLOSE_CONTROL_SPEED);
  if (p.possessing[id] === 1) {
    // The dribble cap is a MIN, not a replacement — but Sprint lifts it: pressing
    // Shift with the ball is a knock-and-run, not a walk. Without the ball you get
    // 6.0/8.5; with it 5.5 (control) or 7.0 (Shift). The speed-aware touch keeps
    // the lead inside foot reach at 7.0 so the ball stays with you. (§3.3, #3/#4)
    cap = Math.min(cap, intent.sprint ? MAX_SPEED_SPRINT_DRIBBLE : MAX_SPEED_DRIBBLE);
  }
  // ⚠ THE CARRY GOVERNOR USED TO CAP HIM HERE — to max(2.5, ballSpeed − 1.5)
  // — whenever he had run off his own ball. Owner: "players should never slow
  // down unless the player stops or stamina is over". It fired constantly (a
  // driven capture caught it engaging every few seconds while dribbling), and
  // from behind the keyboard an unexplained drop to 2.5 m/s mid-dribble is
  // indistinguishable from a bug.
  //
  // The problem it solved is real and has not gone away: the gather PACE-MATCHES
  // the ball to its owner, so a carrier who outruns his own ball drags it along
  // behind him on a leash and nothing can ever take it off him. But the
  // punishment for knocking it too far should be LOSING IT, not being slowed —
  // that is both what football does and the one thing the owner has not ruled
  // out. `releaseBrokenCarry` (called from the tick) now cuts the leash instead.

  // ── THE BOOST (owner, 2026-08-13) ────────────────────────────────────────
  // Multiplies the cap, then the sprint ceiling clamps it. Gated on having
  // stamina left, so an empty bar simply stops buying anything — the button
  // going dead IS the feedback that the bar is flat.
  if (intent.boost > 0 && p.stamina[id]! > 0) {
    cap = Math.min(cap * BOOST_SPEED_MULTIPLIER[intent.boost]!, MAX_SPEED_SPRINT);
  }

  const move = p.skillActive[id]!;
  if (move !== SkillMove.None) cap *= SKILL_SPEED_MUL[move]!;

  // Off balance after losing a shoulder barge (§4.4). A multiplier, not a floor
  // of zero: he keeps steering and keeps moving — see stumbleTimer in types.ts
  // for why taking movement away from a man inside a capsule overlap is the one
  // thing this must not do.
  if (p.stumbleTimer[id]! > 0) cap *= STUMBLE_SPEED_MULTIPLIER;

  if (intent.jockey) {
    cap *= JOCKEY_SPEED_MULTIPLIER;
    if (intent.hasMove) {
      // Shuffling across an attacker is quicker than backpedalling — that is
      // what makes jockeying a real defensive skill and not just "slower". (§4.4)
      const alongFacing = Math.abs(dcos(angleDelta(p.yaw[id]!, intent.desiredHeading)));
      cap *= lerp(1, JOCKEY_LATERAL_MULTIPLIER, 1 - alongFacing);
    }
  } else if (intent.hasMove) {
    // BACKWARD travel is slower than a forward run: full pace out to 120° off
    // the facing (a strafe is an ordinary stride — see the cap's doc comment
    // for the AI box-run measurement that proves capping it is wrong), then
    // sliding to BACKPEDAL_SPEED_CAP at a dead 180°. No full-speed backward
    // sprint. Outside jockey only. In ordinary play the yaw slews onto the
    // movement within a second, so this bites exactly while genuinely giving
    // ground — which is when a real player is slowest.
    const offCos = dcos(angleDelta(p.yaw[id]!, intent.desiredHeading));
    const t = Math.min(1, Math.max(0, (BACKPEDAL_ANGLE_COS - offCos) / (BACKPEDAL_ANGLE_COS + 1)));
    cap *= lerp(1, BACKPEDAL_SPEED_CAP, t);
  }

  return fatigueCeiling(cap * statMultiplier(attr.pace), p.stamina[id]!);
}

/**
 * Accelerate toward the desired velocity; never assign it. (§3.3)
 *
 * The shape of the whole model:
 *   1. Momentum lives in the MOVEMENT HEADING, which rotates at a rate set by
 *      current speed — fast when slow, slow when fast.
 *   2. What you cannot turn, you must scrub. Target speed is scaled by
 *      cos(residual angle), so slamming the stick backwards at a sprint gives a
 *      target of -1 × cap → clamped to 0 → full DECELERATION, while the heading
 *      grinds around at 90°/s and speeds up as the speed bleeds off.
 * That is a real ~1.5s 180°, and it is the point. A player who wants to turn
 * fast has to slow down first — which is exactly what footballers do.
 */
function stepLocomotion(state: WorldState, id: number, aimYaw: number, sliding: boolean): void {
  const p = state.players;
  const attr = state.attributes[id]!;

  // Before anything reads the cap: if he has run off his own ball, he loses it
  // rather than being slowed down for it. (owner: "players should never slow
  // down unless the player stops or stamina is over")
  releaseBrokenCarry(state, id);

  const vx = p.vx[id]!;
  const vz = p.vz[id]!;
  let speed = Math.sqrt(vx * vx + vz * vz);

  // Below MOMENTUM_MIN_SPEED there is no momentum to fight, and datan2 on a
  // near-zero vector is meaningless — adopt the intended heading directly.
  const heading = speed > MOMENTUM_MIN_SPEED
    ? datan2(vz, vx)
    : (intent.hasMove ? intent.desiredHeading : p.yaw[id]!);

  const turnRate = turnRateFor(speed, attr.agility);

  // ── Facing ──────────────────────────────────────────────────────────────
  // Body shape is speed-limited by the same curve: §3.3 names defensive body
  // shape as one of the things that emerges from it, and a sprinter who can
  // snap his shoulders 180° instantly has no body shape to beat. Kick aim is
  // unaffected — that comes from input.aimYaw in resolveKicks (§4.2), not here.
  let targetYaw = aimYaw;
  if (!sliding) {
    if (intent.jockey) {
      const t = findTackleTarget(state, id, JOCKEY_TARGET_RANGE);
      if (t >= 0) targetYaw = datan2(p.pz[t]! - p.pz[id]!, p.px[t]! - p.px[id]!);
    }
    if (inSkillWindup(state, id)) {
      // Sell the feint with the body, or there is nothing for a defender to read.
      targetYaw = wrapAngle(targetYaw + SKILL_BIAS[p.skillActive[id]!]!);
    }
  }
  // A FOOTED STRIKE PIVOTS INTO ITS AIM. The strike clips kick straight
  // ahead, and STRIKE_MIN_FACING_DOT accepts a ball ~105 deg off the
  // shoulders — so an aimed side-kick used to launch the ball sideways off a
  // forward swing (owner: "animations are good only when the kick is in
  // front"). Through the windup and contact window the body turns to the
  // STORED actAimYaw at STRIKE_AIM_ALIGN_RATE instead — the pivot a real
  // striker uses that window for. Tackles keep their own rotation rules, and
  // past the contact window the ordinary facing takes back over so the
  // follow-through settles naturally.
  let strikePivot = false;
  const actk = p.actKind[id]! as ActionKind;
  if (actk !== ActionKind.None && !isTackleKind(actk)) {
    const adef = actionDef(actk);
    if (normalizedActionTime(p.actStartTick[id]!, state.tick, adef) <= adef.contactWindowEnd) {
      targetYaw = p.actAimYaw[id]!;
      strikePivot = true;
    }
  }
  // The ceiling is `turnRate`; what the shoulders DO is the ceiling eased by how
  // far they still have to go, which is what stops the facing being a turret.
  // (owner: "turn him with animations not like a bot rotating" — TURN_APPROACH_GAIN.)
  const facingRate = strikePivot
    ? STRIKE_AIM_ALIGN_RATE
    : facingRateFor(speed, attr.agility, angleDelta(p.yaw[id]!, targetYaw));
  p.yaw[id] = rotateTowards(p.yaw[id]!, targetYaw, facingRate * FIXED_DT);

  // ── Desired velocity ────────────────────────────────────────────────────
  let desired = heading;
  let targetSpeed = 0;
  if (!sliding && intent.hasMove) {
    desired = intent.desiredHeading;
    if (inSkillWindup(state, id)) {
      desired = wrapAngle(desired + SKILL_BIAS[p.skillActive[id]!]!);
    }
    targetSpeed = maxSpeedFor(state, id) * intent.moveMag;
    // A STRIKE PLANTS *AFTER* THE BALL LEAVES. The first attempt at reading
    // `movementLock` damped the whole windup and measured WORSE (2.80 m of
    // pre-release carry became 4.60 m: slowing the runner lets the dribbled
    // ball roll out of contactRange and the strike resolves late). The visual
    // skate the owner keeps seeing, captured frame by frame
    // (MODELS_SHOWCASE/sprint-pass), lives AFTER contact: the swing plays its
    // planted follow-through while the body glides on at sprint pace. So the
    // lock applies only from the contact window on — the ball is gone or
    // going, nothing can whiff — and the striker checks his run through the
    // follow-through exactly like a real player planting out of a hit.
    // Tackles keep their own movement rules.
    const lockKind = p.actKind[id]! as ActionKind;
    if (lockKind !== ActionKind.None && !isTackleKind(lockKind)) {
      const ldef = actionDef(lockKind);
      const ln = normalizedActionTime(p.actStartTick[id]!, state.tick, ldef);
      if (ln >= ldef.contactWindowStart && ln < 1) {
        targetSpeed *= 1 - ldef.movementLock;
      }
    }
  }

  // ── Momentum ────────────────────────────────────────────────────────────
  const newHeading = rotateTowards(heading, desired, turnRate * FIXED_DT);
  const align = dcos(angleDelta(newHeading, desired));
  targetSpeed *= align > 0 ? align : 0;

  // ── THE CARRY CORNER LIMIT (§3.3 / §3.4) ────────────────────────────────
  // "when running with ball holding shift and turn ball gets left" (user).
  //
  // A knock-and-run rides the ball ~0.82 m ahead of the boot. A running line
  // rotating at ω sweeps that lead point sideways at 0.82·ω, and past ~42 °/s
  // that outruns the across-correction the next knock can apply — so the ball
  // leaves the boot's 0.7 m reach, no touch can ever fire again (a knock never
  // fires backwards through his own body), and it settles behind him. Measured
  // before this existed: on a 50 °/s arc — an 8 m radius, an ordinary bend — the
  // ball sat 1.34 m behind him for 246 consecutive ticks and the separation was
  // still GROWING at the end of the tape.
  //
  // The cure is not a tighter touch model. Below DRIBBLE_KNOCK_MIN_SPEED the
  // shepherding spring owns the ball and tracks a 0.6 m lead point every tick:
  // measured, it holds the ball round a 120 °/s arc at every dribbling stat. So a
  // man cornering hard with the ball is simply not knocking and running — he is
  // carrying it in close control, and that is what this makes true. See
  // CARRY_TURN_FULL_PACE_YAW / CARRY_TURN_TIGHT_SPEED for both measured tables.
  //
  // Keyed on the rotation ACHIEVED this tick AND on the carrier actually TRACKING
  // his request (align, i.e. cos of the residual angle), so it only bites on a
  // sustained arc. A stick-slam saturates the turn rate for a few ticks and would
  // otherwise read as a very tight arc, but §3.3 has already charged for that cut
  // through the same cosine one line above — see CARRY_TURN_TRACKING_ALIGN for the
  // measured cost of charging twice. Below MOMENTUM_MIN_SPEED `heading` IS
  // `desired`, so the rotation reads 0 and a pirouette on the spot is untouched.
  // Pure geometry off WorldState: no new state, no RNG draw. (D1)
  if (
    targetSpeed > CARRY_TURN_TIGHT_SPEED &&
    align > CARRY_TURN_TRACKING_ALIGN &&
    isHisCarry(state, id)
  ) {
    const yawRate = Math.abs(angleDelta(newHeading, heading)) / FIXED_DT;
    if (yawRate > CARRY_TURN_FULL_PACE_YAW * DEG2RAD) targetSpeed = CARRY_TURN_TIGHT_SPEED;
  }

  if (targetSpeed > speed) {
    speed = Math.min(speed + ACCELERATION * statMultiplier(attr.acceleration) * FIXED_DT, targetSpeed);
  } else {
    // Deceleration is NOT stat-modulated: §8.1 wants every stat to map to a real
    // parameter, not every parameter to carry a stat. Stopping is physics.
    // A SLIDE carries; a KNOCKDOWN does not. `sliding` is true for both
    // (see its call site — it folds in knockdownTimer so a felled player also
    // loses steering), but they are different physics: a slide tackle is a
    // deliberate committed travel along the grass, while a man who has just
    // been put on the floor should pull up quickly. Applying the slide's
    // gentler braking to knockdowns skidded them far enough to change who
    // ended up nearest the ball, which broke the frontal-standing-tackle
    // possession guarantee in possession-fixes.test.ts.
    // ⚠ THREE DIFFERENT BRAKES, AND THE DISTINCTION IS MEASURED, NOT TIDINESS.
    //
    //   SLIDE   a body on the grass losing speed to friction. Flat, and gentler
    //           than a man on his feet — it carries. (SLIDE_TACKLE_DECELERATION)
    //   FREE STOP  he has asked for NOTHING. This is the one the owner reported
    //           ("dont stop player suddenly, show it really how a player stops")
    //           and the only one that takes the curve, so the last couple of m/s
    //           are a settle rather than a switch.
    //   SCRUB   he is still asking to move and cannot have it — turning through
    //           a residual angle, or under a cap that just dropped. Flat 13.0,
    //           EXACTLY as before, because the carry-recovery model is built on
    //           being able to check hard: softening it cost dribble-weave its
    //           whole weave (9 crossings -> 0, possession lost). See the
    //           STOP_DECELERATION_PEAK block in constants.ts.
    //
    // ⚠ AND A CONTROLLER WE NEVER HEARD FROM IS NOT A STOP HE CHOSE. `!heard` is
    // `readIntent(null)` — a dropped packet, a parked test fixture, a stage-locked
    // walk-in whose position the FSM writes itself. Those keep the flat number,
    // because a longer coast there is not a performance, it is an overshoot: with
    // the curve applied to them, dribble-weave's staged kick-off man coasted an
    // extra ~0.4 m onto the centre spot and took the ball off the carrier at
    // t=32 (possession 5 -> 15, measured). §2's own rule is that a null input
    // means "we never heard", not "he let go", and only the second is this.
    const decel = isSliding(state, id) ? SLIDE_TACKLE_DECELERATION
      : (intent.hasMove || !intent.heard) ? DECELERATION
        : stopRateFor(speed);
    speed = Math.max(speed - decel * FIXED_DT, targetSpeed);
  }

  p.vx[id] = dcos(newHeading) * speed;
  p.vz[id] = dsin(newHeading) * speed;
}

/** §3.3's three effort bands, keyed on actual speed rather than the button. */
function stepStamina(state: WorldState, id: number): void {
  const p = state.players;
  const attr = state.attributes[id]!;
  const vx = p.vx[id]!;
  const vz = p.vz[id]!;
  const speed = Math.sqrt(vx * vx + vz * vz);

  // ── EFFORT IS WHAT DRAINS, NOT INTENT ───────────────────────────────────
  // A player holding a key into the back of a defender is going nowhere and
  // should not be paying for it. So the gate below is his SPEED, and the boost
  // tier only chooses how expensive the running he is actually doing is.
  // `intent` is the module scratch, already filled by readIntent() for THIS
  // player this tick — the same one maxSpeedFor reads a few lines earlier.
  // ⚠ AN EMPTY BAR CANNOT BOOST. Without this the tier still bills at the boost
  // rate against a stamina of 0, which does nothing to the number but does make
  // the drain read as active on a bar that is already flat.
  const tier = p.stamina[id]! > 0 ? intent.boost : 0;

  let rate: number;
  if (speed <= STAMINA_RECHARGE_MAX_SPEED) {
    // Idle or walking — the only states that give it back.
    //
    // ⚠ THIS USED WALK_SPEED_THRESHOLD (1.4) AND MISSED THE WALK BY 0.148 m/s.
    // The client's walk key throttles the move vector by WALK_THROTTLE
    // (0.258), which off a 6.0 run cap is 1.548 m/s — just ABOVE 1.4, so a
    // player holding walk sat in the neutral jog band and recharged NOTHING.
    // Owner, live: "stamina is burning on walk and not getting recharged on
    // walk". He was right about both halves: without the walk key every
    // keyboard step is a full 6 m/s run (which drains), and with it he still
    // got nothing back.
    //
    // The two numbers are unrelated concepts that happened to share a
    // constant — WALK_SPEED_THRESHOLD is the Walk/Jog ANIMATION edge, tuned
    // against foot-sliding on the walk clip, and has no business deciding the
    // stamina economy. Same coincidence as MAX_SPEED_JOG and the Jog/Run clip
    // edge. Separated, so tuning either cannot silently move the other.
    rate = STAMINA_RECHARGE * statMultiplier(attr.stamina);
  } else if (speed <= MAX_SPEED_JOG) {
    // ⚠ A JOG NOW COSTS NOTHING. Owner: "stamina only decreases on Run not
    // walk". It used to fall through to the drain branch and be billed at the
    // full RUN rate, which is why the bar emptied during ordinary play — a
    // player spends most of a match between a walk and a jog.
    //
    // The comment on this function has claimed since it was written that "a jog
    // sits in neither branch... a pace he can hold forever". It never did: the
    // code had two branches and everything above a walk drained. This is the
    // behaviour that was always described, finally implemented.
    rate = 0;
  } else {
    const drain =
      tier >= 2 ? STAMINA_DRAIN_BOOST_2
      : tier === 1 ? STAMINA_DRAIN_BOOST_1
      : STAMINA_DRAIN_RUN;
    // ⚠ THE STAT DIVIDES THE DRAIN, IT DOES NOT MULTIPLY THE RECHARGE ABOVE.
    // Both directions read "a fitter player is better off", and doing it on both
    // ends at once compounds into a stamina stat worth roughly twice its
    // neighbours. Kept as it was: divide going out, multiply coming back.
    rate = -drain / statMultiplier(attr.stamina);
  }

  p.stamina[id] = clamp(p.stamina[id]! + rate * FIXED_DT, 0, STAMINA_MAX);
}

/**
 * Kinematic capsule, collide-and-slide. Players are never dynamic bodies. (§3.3)
 *
 * `freezeHorizontal` (a STAGE-LOCKED set-piece walk-in, rules/stage-lock.ts): the
 * FSM already wrote this player's px/pz for the tick, so integrating his velocity
 * here as well would move him twice. Only the horizontal term is dropped — the
 * vertical one still runs, so gravity, the ground contact and the containment
 * failsafe behave for a staged player exactly as they do for everyone else.
 */
function moveViaPhysics(
  state: WorldState,
  id: number,
  physics: PhysicsWorld,
  freezeHorizontal = false,
): void {
  const p = state.players;
  // Was this player off the ground coming into the move? (i.e. mid-jump). Read
  // BEFORE the move. A rising/hanging jumper is above the epsilon band; a
  // descending one may already be inside it, so a downward vy counts too. A
  // settled player rests below the band with vy EXACTLY 0 (the grounded clause
  // zeroes it, and the collision pitch is flat), so this is false for everyone
  // who never left the ground — the landing branch below is then a no-op.
  // ⚠ WAS A HEIGHT TEST (`py > JUMP_GROUND_EPSILON || vy < 0`) AND FIRED ON
  // EVERY BUMP. The pitch undulates by exactly the epsilon, so crossing a crest
  // at a run counted as a landing and stamped a JUMP_LAND_RECOVERY_S action
  // gather — 0.15 s in which he cannot start an action — several times a run,
  // for no reason a player could see. The gather is for landing from a JUMP, so
  // it asks whether he was in one.
  const wasAirborne = p.jumpAirborne[id] === 1;

  p.vy[id] = p.vy[id]! + GRAVITY * FIXED_DT;
  physics.moveCharacter(
    id,
    freezeHorizontal ? 0 : p.vx[id]! * FIXED_DT,
    p.vy[id]! * FIXED_DT,
    freezeHorizontal ? 0 : p.vz[id]! * FIXED_DT,
    moveOut,
  );
  p.px[id] = moveOut.x;
  p.py[id] = moveOut.y;
  p.pz[id] = moveOut.z;
  // Keep the controller's own verdict. It is the only thing that can tell a
  // player CRESTING the pitch undulation from one who has actually left it —
  // see WorldState.players.grounded for the measurement that forced this.
  p.grounded[id] = moveOut.grounded ? 1 : 0;
  if (moveOut.grounded && p.vy[id]! < 0) {
    p.vy[id] = 0;
    // Touched down — whatever put him in the air, he is out of it now.
    p.jumpAirborne[id] = 0;
    // Landed from a jump: a short gather so hops cannot be chained. (house rule)
    if (wasAirborne) p.actionTimer[id] = JUMP_LAND_RECOVERY_S;
  }

  // Containment failsafe. The world is walled (physics-rapier), so this should
  // be dead code — it exists because a player at y=-4000 is a worse failure
  // than a player pinned to the world edge, and "should" is not a guarantee
  // (seen live: players walking off the ground slab and falling forever).
  // Positions outside the §2.8 volume cannot be encoded on the wire anyway.
  const pMaxX = QUANT_POS_MAX_X - PLAYER_CAPSULE_RADIUS - 1;
  const pMaxZ = QUANT_POS_MAX_Z - PLAYER_CAPSULE_RADIUS - 1;
  if (p.px[id]! > pMaxX) { p.px[id] = pMaxX; if (p.vx[id]! > 0) p.vx[id] = 0; }
  else if (p.px[id]! < -pMaxX) { p.px[id] = -pMaxX; if (p.vx[id]! < 0) p.vx[id] = 0; }
  if (p.pz[id]! > pMaxZ) { p.pz[id] = pMaxZ; if (p.vz[id]! > 0) p.vz[id] = 0; }
  else if (p.pz[id]! < -pMaxZ) { p.pz[id] = -pMaxZ; if (p.vz[id]! < 0) p.vz[id] = 0; }
  if (p.py[id]! < -0.5) { p.py[id] = 0; p.vy[id] = 0; }
}

/**
 * Animation follows physics and never the reverse. (§6.5)
 *
 * Nothing below feeds back into movement: this reads the velocity the sim
 * already produced and picks a clip name for it. Root motion would invert that
 * and make the animation authoritative over position, which is unshippable
 * online — the server would have to run the animation graph to agree with the
 * client about where anyone is.
 */
/** The speed bands, slowest first, and the lower edge of each. Index into both
 *  with the same number so a band and its edge can never drift apart. */
const LOCOMOTION_BANDS = [AnimState.Walk, AnimState.Jog, AnimState.Run, AnimState.Sprint] as const;
/** Lower edge of bands 1..3 — `LOCOMOTION_EDGES[i]` is what band `i+1` needs. */
const LOCOMOTION_EDGES = [WALK_SPEED_THRESHOLD, ANIM_JOG_RUN_EDGE, ANIM_SPRINT_SPEED] as const;

/** Which band a state belongs to, or -1 if it is not a locomotion band at all
 *  (idle, a tackle, a strafe...). Only a player already IN a band gets to hold
 *  it, so arriving from anywhere else uses the plain edges. */
function bandOfAnim(anim: AnimState): number {
  for (let i = 0; i < LOCOMOTION_BANDS.length; i++) if (LOCOMOTION_BANDS[i] === anim) return i;
  return -1;
}

/** The moving-locomotion clip for a player's velocity vs facing: strafe when
 *  shuffling off the facing line, else the speed-band run clip. (§3.3)
 *
 *  `prev` is the player's current state and exists ONLY to hold the band he is
 *  already in — see ANIM_BAND_HYSTERESIS, which documents the 20-flips-a-second
 *  measurement that made it necessary. Reading it keeps the sim pure: `anim`
 *  is already part of WorldState, so it clones, hashes and rewinds with
 *  everything else (D1). */
export function locomotionAnim(
  p: WorldState['players'], id: number, vx: number, vz: number, speed: number, prev: AnimState,
): AnimState {
  const off = angleDelta(p.yaw[id]!, datan2(vz, vx));
  const offCos = dcos(off);
  if (offCos < BACKPEDAL_ANGLE_COS) {
    // Moving mostly AGAINST the facing — giving ground, not running. Before
    // this member existed the renderer answered backward travel with the
    // forward cycle played in reverse (the filmed "rotated forward run").
    return AnimState.Backpedal;
  }
  if (offCos < STRAFE_ANGLE_COS) {
    // Moving well off the way you are facing — shuffling, not running.
    return off > 0 ? AnimState.StrafeLeft : AnimState.StrafeRight;
  }
  // ⚠ THE BAND IS REMEMBERED, NOT READ OFF `prev`.
  //
  // This used to be `bandOfAnim(prev)`, which returns -1 for anything that is
  // not a band — a strafe, a backpedal, a pass, a throw-in hold. The
  // hysteresis then evaporated: `held > i` was false for every band, the
  // player re-entered locomotion on the PLAIN edge, and could drop straight
  // back out of it. Since strafes and touches are constant in a real match,
  // the hysteresis was absent most of the time it was needed.
  //
  // MEASURED, 5-minute AI 11v11 transition histogram: Run<->Jog alone was
  // 2,882 changes (31 % of ALL animation churn), Jog<->Walk 1,405, Run<->Sprint
  // 684 — 54 % of every transition in the match was a player oscillating across
  // a speed-band edge, with StrafeLeft->Run and ThrowInHold->Strafe right
  // behind them. LOCO_DWELL_S does not fix this: a dwell is a RATE LIMIT, so
  // it caps the flip at 4/s and never stops it.
  //
  // `lastBand` persists across non-band states, so a player who strafes for
  // half a second and resumes running is still the runner he was.
  // ── THE CARRY TURN (owner, 2026-08-17: reverted — did not look good) ─────
  // Was: a man turning hard WITH THE BALL played `Running Turn 180` instead of
  // the plain gait. AnimState.CarryTurn and its client-side clip wiring are
  // left in place (dead: nothing here selects it any more) rather than pulled
  // across sim/types.ts + world.ts + the render layer, so this stays a single-
  // file, easily-bisectable change. `p.prevHeading` similarly stays declared
  // and unused; it is already correctly wired into copyPlayerArrays/hashWorld
  // and removing it buys nothing but risk while other sessions are active.
  const held = bandOfAnim(prev) >= 0 ? bandOfAnim(prev) : p.lastBand[id]!;
  let band = 0;
  for (let i = 0; i < LOCOMOTION_EDGES.length; i++) {
    // Climbing into band i+1 costs the full edge; staying in it (or above it)
    // only costs the edge less the hysteresis. A player sitting exactly on an
    // edge therefore keeps whichever band he arrived in instead of alternating.
    const edge = held > i ? LOCOMOTION_EDGES[i]! - ANIM_BAND_HYSTERESIS : LOCOMOTION_EDGES[i]!;
    if (speed > edge) band = i + 1;
  }
  p.lastBand[id] = band;
  return LOCOMOTION_BANDS[band]!;
}

/**
 * Poses produced by playing the ball with the HANDS rather than the boot: the
 * keeper's three distributions (§4.5 roll-out / overarm throw / punt — the punt
 * is a boot but it is set by the same release path and needs the same hold) and
 * Law 15's two-handed throw-in release.
 */
/**
 * Poses that ARE a strike of the ball (or at it) — the swing whose contact the
 * ball's own velocity change came from.
 *
 * One list, two readers: `stepAnim` holds these through their commit window,
 * and `resolveBarge` refuses to stomp one. Duplicating the membership in both
 * places is how the ordering hole below got in, so it lives here instead.
 */
export function isFootedStrikeAnim(anim: number): boolean {
  return (
    anim === AnimState.Punch ||
    anim === AnimState.Kick ||
    anim === AnimState.Pass ||
    anim === AnimState.Shoot ||
    anim === AnimState.Volley ||
    anim === AnimState.Header
  );
}

function isHandReleaseAnim(anim: number): boolean {
  return (
    anim === AnimState.Throw ||
    anim === AnimState.GkRollOut ||
    anim === AnimState.GkThrow ||
    anim === AnimState.GkKick
  );
}

/**
 * Is this player inside the follow-through of a ball HE has just released?
 *
 * THROW_ANIM_HOLD_S is the constant that already exists for this exact job — its
 * own comment in constants.ts is "how long the thrower holds the throw pose". No
 * new tunable, no new state: derived from ball.lastTouchedBy/lastTouchTick.
 */
function inOwnReleaseFollowThrough(state: WorldState, id: number): boolean {
  const b = state.ball;
  return (
    b.lastTouchedBy === id &&
    b.possessedBy !== id &&
    b.lastTouchTick >= 0 &&
    (state.tick - b.lastTouchTick) * FIXED_DT < THROW_ANIM_HOLD_S
  );
}

function stepAnim(state: WorldState, id: number, sliding: boolean, actionLocked: boolean): void {
  const p = state.players;
  const prev = p.anim[id]!;
  const vx = p.vx[id]!;
  const vz = p.vz[id]!;
  const speed = Math.sqrt(vx * vx + vz * vz);

  let next: AnimState;
  if (p.knockdownTimer[id]! > 0) {
    // House rule: decked by a punch/kick (or a bicycle kick's own floored
    // recovery). Checked before `sliding` (which is true during a knockdown) so
    // it reads as Fallen, not a slide recovery.
    //
    // THE LAST SLICE OF THE KNOCKDOWN IS A GET-UP, not more lying down.
    // Review: "when he falls let him stand up, not directly stand up — same with
    // every animation". Before this, `anim` was Fallen for the whole timer and
    // then became a locomotion state on the tick it expired, so the body went
    // from flat on the grass to fully upright in one frame. The render-side
    // cross-fade cannot rescue that: blending a lying pose into a standing one
    // over ~0.15 s reads as the torso snapping vertical, which is precisely what
    // "directly stand up" describes.
    //
    // AnimState.SlideRecover is reused rather than adding a new member: its clip
    // is literally "pushing back up off the ground" (the renderer maps it to
    // Jump_Land for exactly that reason), it already exists on the wire, and a
    // man getting off the floor after a trip and after a slide are the same
    // action. A new state would cost an enum value, a clip mapping and a wire
    // note to express something already present.
    next =
      p.knockdownTimer[id]! <= KNOCKDOWN_GETUP_S ? AnimState.SlideRecover : AnimState.Fallen;
  } else if (
    state.match.phase === MatchPhase.Goal &&
    p.team[id]! !== state.match.restartTeam &&
    speed <= ANIM_IDLE_SPEED
  ) {
    // The scoring side celebrates during the goal dwell (§5.1). restartTeam is
    // the side that will kick off — i.e. the one that just conceded — so its
    // complement is the scorer's team. Gated on being roughly stationary so a
    // player still running it off keeps his locomotion rather than moonwalking
    // through a celebration clip. AnimState.Celebrate existed since the first
    // types.ts and nothing had ever set it: the celebration was a clip with no
    // trigger, which is why a goal looked like nothing happened.
    next = AnimState.Celebrate;
  } else if (sliding) {
    next = p.actionTimer[id]! > SLIDE_TACKLE_RECOVERY_S ? AnimState.SlideTackle : AnimState.SlideRecover;
  } else if (p.jumpAirborne[id] === 1) {
    // Airborne off a Space jump — Jump anim for the whole arc. (house rule)
    //
    // ⚠ NO HEIGHT TEST, DELIBERATELY. This used to read
    // `py > JUMP_GROUND_EPSILON`, and the owner filmed the result as "he
    // randomly jumps": the pitch undulates by PITCH_UNDULATION_M (0.03 m), the
    // epsilon is 0.03 m, and running over a bump therefore cleared it. Adding
    // the controller's `grounded` verdict removed most of it but not all —
    // falling off the FAR SIDE of a crest is genuinely airborne and genuinely
    // above the epsilon. See WorldState.players.jumpAirborne for the trace.
    // A jump is a jump because he JUMPED; the sim knows, so it asks.
    next = AnimState.Jump;
  } else if (actionLocked && (isFootedStrikeAnim(prev) || prev === AnimState.Throw)) {
    // THE STRIKE THAT MOVED THE BALL OUTRANKS THE STUMBLE.
    //
    // This branch used to sit BELOW the stumble, and anim-integrity's soak
    // caught the hole the moment a trajectory change walked onto it: at tick
    // 2428 player 10 kicked the ball while `stumbleTimer` still read 0.45 s, so
    // the ball left his boot at pace while his body played `Stumble` and the
    // strike never appeared. That is precisely "ball movement does not match
    // visible contact" — the one thing a kick pose exists to prevent.
    //
    // Ordering, stated plainly: Fallen/slide/airborne still outrank a strike
    // (you cannot kick from those, and the airborne blocker is enforced
    // elsewhere), but a strike outranks a stumble. The stumble is NOT lost — a
    // barge arms 0.45 s against a strike hold of ~0.22 s, so the off-balance
    // pose plays out the remainder the moment the swing releases, which is the
    // real order of events anyway: he pokes the ball, THEN loses his feet.
    //
    // House rule: hold the swing pose through its commit/recovery window — the
    // footed strikes (Pass/Shoot/Volley/Header) are set by ball.ts at kick
    // resolution with a short actionTimer so the kick animation actually plays.
    //
    // `prev === AnimState.Throw` joined this branch for a DIFFERENT reason than
    // the footed strikes, and it is worth being honest about which: a throw-in
    // taker mid wind-up is not moving (speed ~0), so before this clause every
    // other branch fell through to `speed <= ANIM_IDLE_SPEED → Idle` right here
    // on THIS stage (4). restart-anim.ts (stage 5b, later the same tick) then
    // wrote `AnimState.Throw` back on top every tick of the whole ~1.5 s
    // wind-up (armThrowInRelease's actionTimer, THROW_ANIM_HOLD_S) —
    // Idle → Throw is a real value change, so `setAnimState` stamped a NEW
    // execution EVERY TICK. Confirmed on the running sim, not just reasoned
    // about: `players.animExecId[taker]` incremented on 91/91 ticks of a real
    // throw-in's Ready wind-up while `players.anim[taker]` sat constant at
    // `Throw` the entire time (`scripts/repro-throwin-execid.mjs`). The
    // renderer's `animationDirector.ts drive()` treats ANY execId change as a
    // fresh action and calls `requestAction(..., { interrupt: true })`,
    // restarting the clip from frame zero on every render frame — so a clip
    // that measurably swings `hand_r` 128° when driven once in isolation
    // (`forcePose`/`boneSnapshot`, direct-drive) never advanced past its first
    // instant in a real throw-in, and the taker's arms stayed at his sides for
    // the whole sequence. `isFootedStrikeAnim` does not cover Throw — it is not
    // a footed strike — so it needed its own clause rather than silently riding
    // the existing one. GkThrow/GkKick/GkRollOut do NOT need this: goalkeeper.ts
    // sets their `actionTimer` to 0 the instant they fire (no wind-up to
    // survive), so their follow-through is already covered below by
    // `isHandReleaseAnim(prev) && inOwnReleaseFollowThrough(...)`, which is not
    // gated on `actionLocked` at all.
    next = prev;
  } else if (p.stumbleTimer[id]! > 0) {
    // Off balance after losing a shoulder barge (§4.4). Below Fallen, below
    // the slide/airborne poses (a stumble is the mildest reaction of the set)
    // and below a live strike hold (above), but above everything else, because
    // the whole point is that the hit READS — before this, a barged player kept
    // his running clip and the contact was invisible. Held only while the timer
    // runs, so it can never stick.
    next = AnimState.Stumble;
  } else if (p.skillActive[id]! !== SkillMove.None) {
    next = AnimState.Skill;
  } else if (actionLocked && prev === AnimState.StandingTackle) {
    // A standing tackle is a planted, low-speed challenge. If the player is
    // actually MOVING through it (a lunge on the run), show locomotion so the feet
    // move instead of foot-sliding the static tackle crouch across the pitch —
    // the same rule a moving jockey follows (§1). The tackle MECHANIC is untouched
    // (anim is non-authoritative); only the clip changes.
    next = speed > WALK_SPEED_THRESHOLD ? locomotionAnim(p, id, vx, vz, speed, prev) : AnimState.StandingTackle;
  } else if (isHandReleaseAnim(prev) && inOwnReleaseFollowThrough(state, id)) {
    // A ball played with the HANDS follows through too. (§4.5, Law 15)
    //
    // Owner reports: "the goal keeper not only kicks he can also throw the ball"
    // and "in throw in the ball has to be taken with hand there is no aniamtion".
    // Neither pose could last more than one tick, because the modules that set
    // them run at stage 5 (goalkeeper.ts) and stage 5b (restart-anim.ts) and this
    // function re-derives `anim` from velocity at stage 4 of the very next tick.
    // A 16.7 ms tag is shorter than one render cross-fade step, which is what
    // "there is no animation" looks like from the outside.
    //
    // Deliberately NOT gated on `actionLocked`. A hand release must leave
    // actionTimer at ZERO: stepKeeper reads actionTimer > 0 as "mid-dive or
    // recovering from one" and its recovery branch puts the keeper in
    // AnimState.Fallen, so arming the clock to hold a throw pose would lay him on
    // the floor after every distribution. The window is DERIVED from
    // ball.lastTouchedBy/lastTouchTick instead — the same two already-hashed
    // fields resolveSave's anti-re-gather guard reads — so it clones, hashes and
    // survives a reconciliation rewind for free, with no new WorldState field and
    // nothing on the wire. (§2.4, world.ts, D1)
    next = prev;
  } else if (intent.jockey && speed > WALK_SPEED_THRESHOLD && !actionLocked) {
    // Jockeying while MOVING (above a walk): show locomotion so the feet move,
    // not the static jockey crouch — that clip foot-slides and reads as a player
    // "stuck sitting/crouched during ordinary play" (§1). The low defensive
    // jockey stance is only for a defender holding his ground at walking pace.
    next = locomotionAnim(p, id, vx, vz, speed, prev);
  } else if (intent.jockey && !actionLocked) {
    next = AnimState.Jockey;
  } else if (speed <= ANIM_IDLE_SPEED) {
    // A MILLISECOND'S STOP IS NOT IDLE (owner, 2026-08-14: "when player stop
    // for a milli sec he starts doing idle animation holding his hips").
    // Every dribble touch and direction flip passes through a tick or two of
    // near-zero speed, and an instantaneous gate flashed the hips pose on all
    // of them. He is idle only once he has been still for IDLE_SETTLE_S;
    // until then a player who WAS moving keeps the walk winding down — the
    // settle steps real feet actually take. A player already idle stays idle
    // (no bouncing back to Walk), and a fresh world's lastMoveTick sits deep
    // in the past so kickoff line-ups stand rather than march.
    const still = state.tick - p.lastMoveTick[id]! >= IDLE_SETTLE_TICKS;
    const wasMoving = prev === AnimState.Walk || prev === AnimState.Jog
      || prev === AnimState.Run || prev === AnimState.Sprint
      || prev === AnimState.StrafeLeft || prev === AnimState.StrafeRight
      || prev === AnimState.Backpedal;
    if (!still && wasMoving) {
      next = AnimState.Walk;
    } else {
      // ⚠ FATIGUED IS GONE. Owner, 2026-08-15, having watched it in a capture:
      // "remove that hips holding animation please".
      //
      // The previous note recorded the owner's own earlier instruction — show
      // it only at empty stamina — and that narrowing was done. It was not
      // enough: a hands-on-hips pose reads as a man who has given up, and a
      // footballer waiting for the ball does not stand like that even when he
      // is exhausted. The threshold was never the problem; the pose was.
      //
      // A STANDING PLAYER IS NOW ALWAYS `Idle`. `Fatigued` stays in the clip
      // pack and stays bound in CLIP_FOR_COMMAND — nothing is deleted, so a
      // future exhaustion state can reach it deliberately — but the sim has no
      // path that emits it, which is the only thing that could put it on screen.
      next = AnimState.Idle;
    }
  } else {
    p.lastMoveTick[id] = state.tick;
    next = locomotionAnim(p, id, vx, vz, speed, prev);
  }

  // The transition ALSO stamps a new execution id (§11). `setAnimState` is the
  // change-guard, the blend reset and the stamp in one call, so "the state
  // changed" and "a new execution began" cannot get out of step — which is the
  // failure mode of maintaining the counter next to fifteen scattered writes.
  // Note the ordering: it must run before the else-branch's blend advance, and a
  // NO-OP write here (next === prev, the strike-hold branch) deliberately does
  // NOT stamp — the hold is one execution for its whole life.
  // THE LOCOMOTION DWELL (owner: "make sure all the things are realistic and
  // not forced"). Measured on a 5-minute AI 11v11: 3 751 bursts of 5-6 gait
  // changes per SECOND as headings wobbled across the strafe/backpedal edges
  // and speeds across the band edges - a player permanently mid-blend. A gait
  // may only replace another gait after LOCO_DWELL_S in it; everything else
  // (actions, falls, idle entries, get-ups) switches instantly, because those
  // are events and events must land on time.
  //
  // ⚠ THE DWELL IS MEASURED FROM THE LAST GAIT, NOT THE LAST ANIMATION, and
  // that distinction is the whole fix. It used to require BOTH `next` and
  // `prev` to be gaits and to read `animEnterTick`, so any action passing
  // through punched a hole straight through it:
  //
  //     Walk → PassLeftFooted → Jog → Run
  //            ^ not a gait: dwell skipped AND animEnterTick re-stamped
  //
  // Every pass, touch or challenge therefore handed the player a free instant
  // gait change, and in a match those are constant. MEASURED: p10 cycling
  // Walk/Pass/Jog/Run at 6 changes per second — one every 0.17 s, comfortably
  // inside a 0.25 s dwell that believed it was holding.
  //
  // `lastGaitTick` is stamped only on entering a gait, so an action costs a
  // player nothing and buys him nothing. Coming OUT of an action is still
  // instant — an action ending must land on time, and holding `prev` there
  // would freeze the strike clip — it simply does not reset the clock.
  if (next !== prev && isGait(next)
      && state.tick - p.lastGaitTick[id]! < LOCO_DWELL_TICKS) {
    if (isGait(prev)) next = prev;
  }

  if (next !== prev) {
    p.animEnterTick[id] = state.tick;
    if (isGait(next)) p.lastGaitTick[id] = state.tick;
    setAnimState(p, id, next);
  } else {
    p.animBlend[id] = clamp01(p.animBlend[id]! + FIXED_DT / ANIM_BLEND_TIME_S);
  }
}

/** Tick the committed-action clocks and pay out a skill move's recovery. (§4.6) */
function advanceTimers(state: WorldState, id: number): void {
  const p = state.players;
  if (p.actionTimer[id]! > 0) {
    p.actionTimer[id] = Math.max(0, p.actionTimer[id]! - FIXED_DT);
  }
  // House rule: bleed the victim's knockdown clock (see startViolentConduct).
  if (p.knockdownTimer[id]! > 0) {
    p.knockdownTimer[id] = Math.max(0, p.knockdownTimer[id]! - FIXED_DT);
  }
  // ...and the off-balance clock from a lost shoulder barge (§4.4). Bled here,
  // alongside the knockdown, so both reach zero on the same schedule and the
  // barge that ARMS one (step.ts stage 8) is never also charged for it — the
  // stage-4/stage-8 split means a stumble always pays out its full duration.
  if (p.stumbleTimer[id]! > 0) {
    p.stumbleTimer[id] = Math.max(0, p.stumbleTimer[id]! - FIXED_DT);
  }
  const move = p.skillActive[id]!;
  if (move !== SkillMove.None) {
    const t = p.skillTimer[id]! - FIXED_DT;
    if (t <= 0) {
      p.skillTimer[id] = 0;
      p.skillActive[id] = SkillMove.None;
      // §4.6: "each must be counterable and have a recovery cost". This is the
      // cost — you finish the elastico on the floor of your own commitment, and
      // cannot chain straight into another one.
      p.actionTimer[id] = skillDuration(move, state.attributes[id]!.agility) * SKILL_RECOVERY_FRACTION;
    } else {
      p.skillTimer[id] = t;
    }
  }
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * One player, one tick. Called from step.ts stage 4 for every active player,
 * human or AI — this function cannot tell them apart, and that is the guarantee
 * §10.1 is built on.
 */
export function stepPlayerController(
  state: WorldState,
  id: number,
  input: InputCommand | null,
  out: StepResult,
  physics: PhysicsWorld,
): void {
  const p = state.players;

  advanceTimers(state, id);
  readIntent(input);

  // Sampled BEFORE actions run: what you may start depends on the state you
  // arrived in, not the one you are about to create. A knocked-down victim
  // (house rule) is as locked out of acting as one mid-recovery — and so is a
  // man still off balance from a lost barge (§4.4): "brief loss of control"
  // means you cannot lunge into a tackle or start a trick out of a stumble. He
  // can still MOVE, which is the difference from a knockdown.
  const wasLocked =
    p.actionTimer[id]! > 0 || p.knockdownTimer[id]! > 0 || p.stumbleTimer[id]! > 0;
  if (input !== null && !wasLocked) {
    tryStartAction(state, id, input, out);
    // Jump is lowest priority: it only fires if no ground action (tackle/skill)
    // claimed this tick, so you cannot slide and leap at once. (house rule)
    tryJump(state, id, input);
    tryIntercept(state, id);
  }

  const actionLocked = p.actionTimer[id]! > 0;
  // A knockdown reuses the slide-recovery locomotion path: no steering, decelerate
  // to a stop from the knockback, facing left where it lands.
  const sliding = isSliding(state, id) || p.knockdownTimer[id]! > 0;

  // A set-piece walk-in owns this player's locomotion for this tick — position,
  // velocity AND facing were all authored by rules/fsm.ts applyStageTargets at
  // stage 2 (rules/stage-lock.ts explains the seam and why it is not WorldState).
  //
  // GATING his locomotion here is what lets that velocity be TRUE. The walk-in
  // used to zero it so this stage could not integrate it a second time, and a
  // staged player therefore translated 0.1 m per tick while |v| = 0 told stepAnim
  // below to show AnimState.Idle: the reported dragging. Everything else in this
  // function still runs for him — timers bleed, stamina bands off his real
  // effort, and stepAnim reads the honest velocity and picks a real gait.
  const stageLocked = isStageLocked(state, id);
  if (!stageLocked) {
    // A null input means we never heard from this controller (step.ts): hold the
    // last facing rather than snapping to yaw 0, which would be a visible spin.
    stepLocomotion(state, id, input !== null ? input.aimYaw : p.yaw[id]!, sliding);
  }
  stepStamina(state, id);
  moveViaPhysics(state, id, physics, stageLocked);
  stepAnim(state, id, sliding, actionLocked);
}

// ---------------------------------------------------------------------------
// Player-player separation (§3.3)
// ---------------------------------------------------------------------------

/**
 * FIFA-style shoulder barge, resolved out of the CONTACT — no input bit. (§3.3, §4.4)
 *
 * Review, verbatim: "enable collision and make other players fall or get hurt use
 * their hands to push like same like fifa". resolveTackle/tripVictim answer the
 * tackle half; this is the jostle-for-position half.
 *
 * ── Why contact and not a button ────────────────────────────────────────────
 * InputFlags is already at bit 17 and past the 16-bit wire field (types.ts), so a
 * new flag costs a PROTOCOL_VERSION bump, a codec widening and a keybind — for a
 * mechanic that has no dedicated button in FIFA either. You barge by running your
 * shoulder into somebody, and that is exactly the state this function already has
 * in hand.
 *
 * ── The model ───────────────────────────────────────────────────────────────
 * A clash is judged on each man's momentum ALONG THE CONTACT NORMAL, weighted by
 * Strength. The loser is shoved off his line, goes off balance (stumbleTimer, NOT
 * knockdownTimer — a fair charge floors nobody), and if the winner arrived hard
 * enough and the loser had the ball, the ball comes off him through the ordinary
 * §14 loose-ball path.
 *
 * ── Determinism ─────────────────────────────────────────────────────────────
 * Zero RNG draws: every branch is a comparison on the contact geometry and the
 * static attributes, exactly like tripVictim. Adding an RNG draw here would be
 * the worst possible place for one — this runs up to 231 times a tick, so the
 * stream consumption would depend on how crowded the pitch is (D1).
 */
function resolveShoulderBarge(
  state: WorldState,
  i: number,
  j: number,
  nx: number,
  nz: number,
  si: number,
  sj: number,
): void {
  const p = state.players;

  // A man already on the floor can neither barge nor be barged. Keepers are out
  // of scope entirely: Law 12 protects a keeper in control of the ball, and
  // goalkeeper.ts owns his motion outright — a stumbleTimer on a GK would not
  // slow him, so setting one would be a lie in the state.
  if (p.knockdownTimer[i]! > 0 || p.knockdownTimer[j]! > 0) return;
  if (state.positions[i] === 'GK' || state.positions[j] === 'GK') return;

  // Each man's momentum INTO the contact, along the shared normal (which points
  // i → j). alongI + alongJ is EXACTLY the pair's closing speed, so one gate
  // answers "is this a clash or a lean".
  const alongI = p.vx[i]! * nx + p.vz[i]! * nz;
  const alongJ = -(p.vx[j]! * nx + p.vz[j]! * nz);
  if (alongI + alongJ < BARGE_MIN_CLOSING_SPEED) return;

  // Law 12's fair-charge condition: the clash must be a challenge FOR the ball,
  // which is what keeps a barge legal and stops it firing as an off-ball body
  // check. Measured from the contact midpoint — the capsules are overlapping, so
  // it is within 0.3 m of either man, and it is symmetric (no i/j bias).
  const mx = (p.px[i]! + p.px[j]!) * 0.5;
  const mz = (p.pz[i]! + p.pz[j]!) * 0.5;
  if (distXZ(mx, mz, state.ball.px, state.ball.pz) > BARGE_CONTEST_RADIUS) return;

  // Who wins, and by enough to matter — see BARGE_DOMINANCE_RATIO for why a
  // dead-even clatter must resolve to nobody rather than to an array-order
  // tie-break.
  const forceI = (alongI > 0 ? alongI : 0) * si;
  const forceJ = (alongJ > 0 ? alongJ : 0) * sj;
  let winner: number;
  let loser: number;
  let winnerDrive: number;
  if (forceI > forceJ * BARGE_DOMINANCE_RATIO) {
    winner = i; loser = j; winnerDrive = alongI;
  } else if (forceJ > forceI * BARGE_DOMINANCE_RATIO) {
    winner = j; loser = i; winnerDrive = alongJ;
  } else {
    return;
  }

  // Refractory window. While the loser is still off balance from the last barge,
  // a sustained overlap may NOT re-arm it. Without this, a pair locked together
  // would refresh the stumble every tick and stay permanently slowed — the exact
  // deadlock this whole module is written to avoid.
  if (p.stumbleTimer[loser]! > 0) return;

  const dir = loser === j ? 1 : -1;

  // A HEAVY charge takes the man down for real, not just off balance. See
  // BARGE_KNOCKDOWN_SPEED_GATE: this is the exact same "clean, legal, ball-
  // first challenge that floors a man" picture TACKLE_CLEAN_FELL_SPEED already
  // covers for a slide, so it reuses the same primitive — `tripVictim` sets the
  // fall direction from the LOSER's own momentum (or, standing still, the
  // WINNER's line — `winner` is passed as `id` below), plants him with
  // `knockdownTimer`, and starts `AnimState.Fallen`. No `reportFoul` call
  // anywhere on this path: Law 12's fair-charge gate above already means this
  // was a legitimate challenge for the ball, exactly like the clean slide.
  if (winnerDrive >= BARGE_KNOCKDOWN_SPEED_GATE) {
    tripVictim(state, winner, loser, BARGE_KNOCKDOWN_S);
  } else {
    const balance = statMultiplier(state.attributes[loser]!.balance);
    // The push — shoulder into shoulder, forearm into chest. `dir` takes it AWAY
    // from the winner: n points i → j, so j is shoved along +n and i along −n.
    // An instantaneous Δv is right here for the same reason the slide lunge and the
    // punch knockback are: §3.3's no-teleport rule governs LOCOMOTION, and a
    // collision is a discrete impulsive event, not locomotion.
    const shove = BARGE_SHOVE_SPEED / balance;
    p.vx[loser] = p.vx[loser]! + nx * dir * shove;
    p.vz[loser] = p.vz[loser]! + nz * dir * shove;

    // Off balance. Steering is deliberately LEFT INTACT (see stumbleTimer's doc in
    // types.ts) — only the speed cap and the right to START an action go. A trick
    // does not survive being barged, which is §4.6's counterability promise.
    p.stumbleTimer[loser] = BARGE_STUMBLE_S / balance;
    p.skillActive[loser] = SkillMove.None;
    p.skillTimer[loser] = 0;
    // A LIVE STRIKE HOLD IS NOT STOMPED.
    //
    // This runs at stage 8, AFTER ball.ts has resolved kicks — so barging a man
    // on the tick he struck the ball used to overwrite the swing pose he had just
    // been given, and the ball left his boot at pace while his body played
    // `Stumble`. anim-integrity's soak caught it at tick 2428 (player 10, ball
    // kicked, `stumbleTimer` 0.45, anim 31): "ball movement with no visible
    // contact", which is the one thing a strike pose exists to prevent.
    //
    // The MECHANIC is untouched — the shove landed above and `stumbleTimer` is
    // armed regardless, so the speed cap, the lost skill move and the block on
    // starting a new action all apply exactly as before. Only the POSE waits:
    // stepAnim's stumble branch picks it up the moment the strike hold expires
    // (a barge arms 0.45 s against a ~0.22 s hold), which is the true order of
    // events anyway — he gets his shot away, THEN loses his feet.
    if (!(p.actionTimer[loser]! > 0 && isFootedStrikeAnim(p.anim[loser]!))) {
      // Unconditional: a second barge while still off balance is a second shove,
      // and it must re-play as one rather than being absorbed into the first.
      beginAnimAction(p, loser, AnimState.Stumble);
    }
  }

  // A hard charge on the man WITH the ball knocks it off him. Gated on the
  // WINNER's own drive into the contact (not the pair's closing speed) so a
  // shoulder can never be a cheaper way to win the ball than a properly-timed
  // challenge (§4.4). It routes through the same teardown a poke uses, so
  // updatePossession runs the ordinary open contest next tick — nothing new
  // decides who ends up with it (§14).
  if (state.ball.possessedBy === loser && winnerDrive >= BARGE_BALL_LOOSE_SPEED_GATE) {
    const b = state.ball;
    const pvx0 = b.vx, pvy0 = b.vy, pvz0 = b.vz;
    // OPPOSITE the shove, which is the whole picture of being barged off the
    // ball: the MAN is driven off his line and the ball is not, so the two leave
    // the contact on opposing vectors. Sending it the SAME way he is pushed keeps
    // it travelling with him and the next-tick contest simply hands it back — a
    // mechanic that fires and changes nothing. It is the SEPARATION, not the exit
    // speed, that makes this a genuine §14 contest.
    //
    // A pure function of the contact geometry: there is no side to pick, hence no
    // RNG draw, hence no way for this to shift the shared stream (D1).
    b.vx = -nx * dir * BARGE_BALL_LOOSE_EXIT_SPEED;
    b.vz = -nz * dir * BARGE_BALL_LOOSE_EXIT_SPEED;
    b.vy = 0;
    // A shoulder is body contact, not a foot touch — tagged as such so §3.2's
    // attribution trace stays honest.
    traceBallSince(state, BallVelocityCause.PlayerBodyContact, winner, pvx0, pvy0, pvz0);
    releaseBallToContest(state, winner);
  }
}

/**
 * Soft shoulder-to-shoulder separation, weighted by Strength. (§3.3, §4.4)
 *
 * Two rules, and they are in tension on purpose:
 *   "Prevent player interpenetration" — so there is a positional term.
 *   "NEVER hard-lock two capsules"    — so it is a soft constraint, not a
 *                                       projection. Two players who genuinely
 *                                       overlap ease apart over a few ticks.
 * A hard positional lock is what reads as rubber-banding online: the server
 * resolves an overlap the client never predicted, the correction exceeds
 * POSITION_EPSILON, and both players get yanked. Soft separation converges to
 * the same place without a correction anyone can see.
 *
 * The shoulder challenge of §4.4 is not a separate mechanic — it IS this
 * function, read at speed. Two players running alongside are permanently
 * resolving a strength-weighted contest, and the loser drifts off his line and
 * loses a yard. Nothing needs to fire, and nothing needs an animation.
 *
 * PLAYER_SEPARATION_IMPULSE is applied as an ACCELERATION (m/s² over dt), not as
 * an instantaneous Δv. At 4.0 m/s² a full overlap separates firmly over a few
 * hundred ms; as an instant 4 m/s kick it would fling players apart on contact.
 * §3.3 asks for a soft push, and only one of those two readings is soft.
 */
export function stepPlayerCollisions(state: WorldState): void {
  const p = state.players;
  const minDist = PLAYER_CAPSULE_RADIUS * 2;

  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    for (let j = i + 1; j < MAX_PLAYERS; j++) {
      if (p.active[j] !== 1 || p.sentOff[j] === 1) continue;

      const dx = p.px[j]! - p.px[i]!;
      const dz = p.pz[j]! - p.pz[i]!;
      let dist = Math.sqrt(dx * dx + dz * dz);
      if (dist >= minDist) continue;

      let nx: number;
      let nz: number;
      if (dist < 1e-6) {
        // Exactly coincident: pick a stable arbitrary UNIT axis and treat it as
        // maximal overlap. Deterministic (an RNG draw here would burn the stream
        // on a degenerate case that only happens at spawn or a rare pile-up). It
        // must be a UNIT normal — the old code divided dx=1 by dist=1e-6, which
        // fabricated a 1e6-long "normal" and, times the separation impulse, flung
        // the pair to −60000 (the encodable-volume soak breach at tick 3041).
        nx = 1;
        nz = 0;
        dist = 0;
      } else {
        nx = dx / dist;
        nz = dz / dist;
      }

      const ai = state.attributes[i]!;
      const aj = state.attributes[j]!;
      const si = statMultiplier(ai.strength);
      const sj = statMultiplier(aj.strength);
      const total = si + sj;
      // The stronger player gives less ground. Shares sum to 1, so the pair's
      // momentum is conserved and neither can be pushed twice.
      const shareI = sj / total;
      const shareJ = si / total;

      const penetration = minDist - dist;
      const push = PLAYER_SEPARATION_IMPULSE * (penetration / minDist) * FIXED_DT;

      // Balance is what keeps you on your line under contact. (§8.1)
      const bi = push / statMultiplier(ai.balance);
      const bj = push / statMultiplier(aj.balance);
      p.vx[i] = p.vx[i]! - nx * bi * shareI;
      p.vz[i] = p.vz[i]! - nz * bi * shareI;
      p.vx[j] = p.vx[j]! + nx * bj * shareJ;
      p.vz[j] = p.vz[j]! + nz * bj * shareJ;

      // Soft positional term. Fractional on purpose — see the header.
      const corr = penetration * PLAYER_SEPARATION_POSITION_CORRECTION;
      p.px[i] = p.px[i]! - nx * corr * shareI;
      p.pz[i] = p.pz[i]! - nz * corr * shareI;
      p.px[j] = p.px[j]! + nx * corr * shareJ;
      p.pz[j] = p.pz[j]! + nz * corr * shareJ;

      // Everything below is between OPPONENTS only: teammates brushing past each
      // other separate and nothing else happens.
      if (p.team[i]! === p.team[j]!) continue;

      // ── Shoulder BARGE (§3.3, §4.4) ───────────────────────────────────
      // A discrete clash — two men driving into each other while contesting the
      // ball. Runs BEFORE the alongside challenge below because they answer
      // different questions: a barge is a collision (opposed headings), the
      // challenge is a sustained duel (aligned headings), so at most one of the
      // two ever fires for a given pair on a given tick.
      resolveShoulderBarge(state, i, j, nx, nz, si, sj);

      // ── Shoulder challenge (§4.4) ─────────────────────────────────────
      // Only at pace, and only when running alongside.
      const vi = Math.sqrt(p.vx[i]! * p.vx[i]! + p.vz[i]! * p.vz[i]!);
      const vj = Math.sqrt(p.vx[j]! * p.vx[j]! + p.vz[j]! * p.vz[j]!);
      if (vi <= MAX_SPEED_JOG || vj <= MAX_SPEED_JOG) continue;
      const alongside = (p.vx[i]! * p.vx[j]! + p.vz[i]! * p.vz[j]!) / (vi * vj);
      if (alongside < SHOULDER_ALIGN_DOT) continue;

      // Strength decides, Balance resists, and the loser pays in speed — the
      // yard he loses is the whole point of the challenge.
      const contest = si / sj; // >1 ⇒ i is stronger
      if (contest > 1) {
        const drop = SHOULDER_CHALLENGE_SPEED_PENALTY * (contest - 1) * FIXED_DT / statMultiplier(aj.balance);
        const scale = Math.max(0, 1 - drop / vj);
        p.vx[j] = p.vx[j]! * scale;
        p.vz[j] = p.vz[j]! * scale;
      } else if (contest < 1) {
        const drop = SHOULDER_CHALLENGE_SPEED_PENALTY * (1 / contest - 1) * FIXED_DT / statMultiplier(ai.balance);
        const scale = Math.max(0, 1 - drop / vi);
        p.vx[i] = p.vx[i]! * scale;
        p.vz[i] = p.vz[i]! * scale;
      }
    }
  }
}
